import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import RicercaEstesaAnnuncio from './km/RicercaEstesaAnnuncio.jsx';
import ResourceCvButton from './km/ResourceCvButton.jsx';
import ResourceDettaglio from './km/ResourceDettaglio.jsx';
// unificazione 29/09 (UC-SA-01/WF-MF-02): l'invio del Self-Assessment è
// ora integrato direttamente nella tabella qui sotto (ScreenCandidatiAnn),
// non più delegato a un pannello D1 separato. HrSelfAssessmentConsole.jsx
// resta nel progetto (Console generale non lo usa, nessun altro punto
// d'ingresso lo richiama) ma non è più importato qui.
//
// V32 (AF v2.0 §4.1, regola vincolante): se alla risorsa è già stato
// inviato un Self-Assessment "recentemente" (soglia di sistema, default 30
// giorni) non se ne invia un secondo ravvicinato. Rimesso il 29/09 dopo
// averlo tolto per errore — la soglia hardcoded (kmLabels.SOGLIA_INVIO_
// RECENTE_GIORNI) è lo scostamento dall'AF, non il controllo in sé.
import { invioRecente, SOGLIA_INVIO_RECENTE_GIORNI } from './km/kmLabels.js';
import { KM_CONFIG } from '../api/config.js';
import { useRealKmClient } from '../lib/useRealKmClient.js';

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
// Stessa mappa già usata in HrSelfAssessmentConsole.jsx (Console HR) —
// etichette discorsive richieste dal SAL del 25/09, coerenti con la
// tabella R1-R4 già verificata contro l'Analisi Funzionale (§3).
// 28/09: etichette riallineate ai nomi degli stati dell'AF v2.0 §7.1.
const STATO_RISORSA_LABEL = {
  R1: 'Inbound',
  R2: 'In qualifica',
  R3a: 'Proponibile mai staffato',
  R3b: 'Collaboratore attualmente staffato',
  R3c: 'Collaboratore ex-staffato',
  R4: 'Da cancellare',
};

// Condizioni dichiarate nel Self-Assessment (06/10, colonne del profilo
// dalla migrazione 036), esposte riga per riga da /hr/longlist. Le scelte
// chiuse arrivano come codici canonici: qui le etichette. I testi liberi
// (area geografica, preavviso, aspettativa economica) si mostrano come sono.
const CONDIZIONI_SA = ['disponibile_opportunita', 'tipo_rapporto', 'sede', 'area_geografica', 'trasferte', 'preavviso', 'aspettativa_economica'];
const ETICHETTE_CONDIZIONI = {
  disponibile_opportunita: { si: 'Sì', no: 'No', solo_specifici: 'Solo progetti specifici' },
  sede: { on_site: 'On-site', remoto: 'Remoto', ibrido: 'Ibrido' },
  trasferte: { si: 'Sì', no: 'No', occasionale: 'Occasionali' },
  // 03/11 — migration 059 (Q-005): tempo indeterminato e determinato distinti;
  // «tempo» resta per chi ha detto solo «da dipendente» (e per le risposte
  // date prima del 2/11). «indifferente» dalla migration 056. Il filtro della
  // longlist usa queste stesse voci: cercando indeterminato o determinato il
  // server include anche «tempo» e «indifferente».
  tipo_rapporto: { piva: 'P.IVA', tempo_indeterminato: 'Tempo indeterminato', tempo_determinato: 'Tempo determinato', tempo: 'Dipendente (non precisato)', somministrazione: 'Somministrazione', altro: 'Altro', indifferente: 'Indifferente' },
};
// Filtri lato server su /hr/longlist: parametro → [etichetta, campo].
const FILTRI_CONDIZIONI = [
  ['disponibile', 'Disponibile', 'disponibile_opportunita'],
  ['sede', 'Sede', 'sede'],
  ['trasferte', 'Trasferte', 'trasferte'],
  ['tipo_rapporto', 'Rapporto', 'tipo_rapporto'],
];
function etichettaCondizione(campo, valore) {
  if (valore === null || valore === undefined || valore === '') return '';
  return ETICHETTE_CONDIZIONI[campo]?.[valore] || String(valore);
}

const T = {
  bg:        '#F8F7F4',
  surface:   '#FFFFFF',
  surface2:  '#F1F0ED',
  border:    'rgba(0,0,0,0.10)',
  borderMd:  'rgba(0,0,0,0.18)',
  txt:       '#1A1916',
  txt2:      '#6B6A65',
  txt3:      '#A3A19C',
  accent:    '#185FA5',
  accentBg:  '#E6F1FB',
  accentTxt: '#0C447C',
  greenBg:   '#EAF3DE',
  greenTxt:  '#27500A',
  amberBg:   '#FAEEDA',
  amberTxt:  '#633806',
  redBg:     '#FCEBEB',
  redTxt:    '#791F1F',
  grayBg:    '#F1F0ED',
  grayTxt:   '#444441',
  font:      "'Segoe UI', system-ui, -apple-system, sans-serif",
};

/* ═══════════════════════════════════════════════════════════
   CLUSTER CONFIG
   Confermato con audit su data_sources.metadata_json ->> 'Profilo
   professionale' (dropdown su Altamira, 19 valori puliti + ~33% vuoto
   dovuto a schede candidato storiche/incomplete, non a un bug del
   campo). È un attributo del CANDIDATO, non dell'annuncio — per
   questo un candidato mantiene lo stesso cluster indipendentemente
   da quale annuncio del run sta coprendo.
   Daniele deve esporre la colonna nella vista DB come
   "profilo_professionale" (vedi CREATE OR REPLACE VIEW concordato);
   multifitting_api.py deve includerla nel payload di /nightly.
   Finché il campo non arriva (key assente da TUTTE le righe), la UI
   mostra un piccolo avviso per sviluppatori; se invece arriva ma è
   vuoto per un candidato specifico, è un caso atteso (scheda
   incompleta) e va mostrato come categoria normale, senza allarmi.
═══════════════════════════════════════════════════════════ */
const CLUSTER_FIELD = 'profilo_professionale';
const CLUSTER_UNSET_LABEL = 'Non classificato — scheda incompleta';

/* ═══════════════════════════════════════════════════════════
   MAPPATURA PROFILI CONSIP
   Fonte: tabella DB consip_profile_embeddings (27 profili,
   fonte ufficiale usata per il matching semantico) — NON il
   capitolato PDF, che è più esteso ma statico/non collegato al
   sistema. Chiave = valore esatto di profilo_professionale.
   Valore = slug in consip_profile_embeddings + flag approx per
   i match non testualmente esatti (da validare con Giuseppe/SB).
   Profili candidato assenti da questa mappa (Manager, IT Service
   Manager, Service Desk Specialist, Digital Media Consultant,
   ICT Junior Consultant, Stagista/Neolaureato, HR Consultant,
   Società) non hanno un profilo corrispondente nella tabella —
   nessun dato inventato per coprirli.
═══════════════════════════════════════════════════════════ */
const CONSIP_SLUG_MAP = {
  'PMO o Project Manager':               { slug: 'project_manager',        approx: false },
  'ICT Senior Consultant':                { slug: 'ict_consultant_senior',  approx: false },
  'Developer':                            { slug: 'developer',              approx: false },
  'System Administrator':                 { slug: 'sysadmin',               approx: false },
  'UX UI Designer':                       { slug: 'ux_designer',            approx: false },
  'Testing Specialist':                   { slug: 'test_specialist',        approx: false },
  'DBA':                                  { slug: 'database_specialist',    approx: false },
  'Analyst':                              { slug: 'business_analyst',       approx: true  },
  'Solution & Infrastructure Architect':  { slug: 'enterprise_architect',   approx: true  },
  'AI Expert':                            { slug: 'ai_strategy_advisor',    approx: true  },
  'Business Consultant':                  { slug: 'digital_consultant',     approx: true  },
};

// Scala di qualità del fitting, dal migliore al peggiore. Usata per trovare
// il fitting più alto raggiunto tra un gruppo di candidati (non il più
// frequente — vedi bestFitting()).
const FITTING_RANK = ['OTTIMO', 'BUONO', 'SUFFICIENTE', 'PARZIALE', 'INADEGUATO'];

function bestFitting(rows) {
  const present = new Set(rows.map(r => r.fitting));
  return FITTING_RANK.find(f => present.has(f)) || '—';
}

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */
function formatRunDate(ts) {
  if (!ts) return '—';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      timeZone: 'Europe/Rome',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    }).format(new Date(ts));
  } catch { return String(ts).slice(0, 10); }
}

// Formatta un intervallo [minDate, maxDate] di run_date. Se le due date
// coincidono (stesso giorno), mostra solo una data invece di un intervallo
// ridondante — utile ora che i dati non sono più filtrati su un run singolo
// ma su un periodo che può contenere una o più date di run diverse.
function formatDateRange(minTs, maxTs) {
  if (!minTs && !maxTs) return '—';
  const minLabel = formatDateLabel(minTs);
  const maxLabel = formatDateLabel(maxTs);
  if (minLabel === maxLabel) return minLabel;
  return `${minLabel} – ${maxLabel}`;
}

// Data e ora brevi per "Stato ultimo SA" (es. "27/09, 16:08") — portata da
// HrSelfAssessmentConsole.jsx col resto della logica di invio, 29/09.
function formatDataOra(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      timeZone: 'Europe/Rome',
      day: '2-digit', month: '2-digit', year: 'numeric'
    }).format(new Date(dateStr));
  } catch { return dateStr; }
}

// Estrae testo semplice da un campo HTML (es. job_text da Altamira),
// usato per anteprime troncate dove non vogliamo renderizzare markup.
function htmlToText(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

// Sanificazione minima prima di iniettare HTML nel DOM (dangerouslySetInnerHTML):
// rimuove script, handler inline (onClick=...) e URL javascript:. Il contenuto
// proviene da job_text su Altamira (interno, non user-generated), quindi è una
// protezione difensiva aggiuntiva, non l'unica barriera.
function sanitizeJobHtml(html) {
  if (!html) return '';
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript:/gi, '');
}

function avatarColors(name) {
  const colors = [
    ['#E6F1FB','#0C447C'],['#EAF3DE','#27500A'],['#FAEEDA','#633806'],
    ['#FCEBEB','#791F1F'],['#F1F0ED','#444441'],['#E8F5FF','#1A5FA8'],
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return colors[h % colors.length];
}

function Avatar({ name, size = 30 }) {
  const initials = name ? name.split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase() : '?';
  const [bg, fg] = avatarColors(name || '');
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.33, fontWeight: 600, flexShrink: 0,
    }}>{initials}</div>
  );
}

function ScoreFill({ score }) {
  const color = score >= 80 ? T.accent : score >= 60 ? '#F59E0B' : '#EF4444';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, minWidth:60, height:5, borderRadius:3, background:T.surface2, border:`0.5px solid ${T.border}` }}>
        <div style={{ width:`${score}%`, height:'100%', borderRadius:3, background: color, transition:'width 0.3s' }} />
      </div>
      <span style={{ fontSize:13, fontWeight:600, minWidth:24 }}>{score}</span>
    </div>
  );
}

function FittingBadge({ fitting }) {
  const map = {
    OTTIMO:      [T.greenBg,  T.greenTxt],
    BUONO:       ['#d4edda',  '#155724'],
    SUFFICIENTE: [T.amberBg,  T.amberTxt],
    PARZIALE:    ['#fde8d8',  '#7d3b00'],
    INADEGUATO:  [T.redBg,    T.redTxt],
  };
  const [bg, fg] = map[fitting] || [T.grayBg, T.grayTxt];
  return (
    <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500, background:bg, color:fg, whiteSpace:'nowrap' }}>
      {fitting || '—'}
    </span>
  );
}

/* 23/10 — SAL 16/10, azione 24: «Voto HR» esposto in lista, distinto dalla
   valutazione di fitting. È il campo «Voto CV» di Altamira (voto_cv),
   confermato il 23/10: un giudizio complessivo sulla persona, non
   l'aderenza all'annuncio. Per questo non usa i colori del FittingBadge
   (che ha valori simili, «OTTIMO», «BUONO»): chip neutra a contorno.
   Scala di Altamira, la stessa dell'ordinamento della Console HR. */
const VOTO_HR_ORDINE = ['top', 'ottimo', 'molto buono', 'buono', 'discreto', 'sufficiente', 'scarso', 'no fit', 'inaffidabile'];

function rangoVotoHr(voto) {
  if (!voto) return null;
  const i = VOTO_HR_ORDINE.indexOf(String(voto).trim().toLowerCase());
  return i === -1 ? VOTO_HR_ORDINE.length : i;
}

function VotoHrBadge({ voto }) {
  if (!voto) return <span style={{ color:T.txt3 }} title="Voto HR non disponibile">—</span>;
  return (
    <span title="Voto HR (Altamira): giudizio complessivo sulla persona, indipendente dal fitting"
      style={{ padding:'1px 8px', borderRadius:6, fontSize:11.5, border:`0.5px solid ${T.border}`, background:T.surface, color:T.txt, whiteSpace:'nowrap' }}>
      {voto}
    </span>
  );
}

function StatusBadge({ status }) {
  // "REUSED" — corretto il 24/09, verificato nel commento sorgente di
  // run_altamira_job_multifitting.py: "score già presente in QUALSIASI
  // scope → copia, status=REUSED, zero OpenAI". NON significa "vecchio
  // screening di giugno" (ipotesi errata scritta qui in precedenza,
  // basata su una correlazione osservata nei dati di allora, che oggi
  // non regge più — probabilmente perché i dati sono cambiati nel
  // frattempo). Significa solo: punteggio già calcolato in un run
  // precedente per questa stessa coppia candidato+annuncio, riusato per
  // risparmiare la chiamata LLM — indipendentemente da quanto è vecchio.
  const map = {
    OK:     [T.greenBg,  T.greenTxt,  'OK'],
    REUSED: [T.accentBg, T.accentTxt, 'Storico'],
    ERROR:  [T.redBg,    T.redTxt,    'Errore'],
  };
  const [bg, fg, lbl] = map[status] || [T.grayBg, T.grayTxt, status];
  return <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500, background:bg, color:fg }}>{lbl}</span>;
}

function Rank({ n }) {
  const styles = {
    1: { background:'#FAEEDA', border:'0.5px solid #EF9F27', color:'#633806' },
    2: { background:'#F1F0ED', border:`0.5px solid #B4B2A9`, color:'#3C3A36' },
    3: { background:'#FAECE7', border:'0.5px solid #F0997B', color:'#712B13' },
  };
  const s = styles[n] || { background:T.surface2, border:`0.5px solid ${T.border}`, color:T.txt2 };
  return (
    <div style={{ width:24, height:24, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600, ...s }}>{n}</div>
  );
}

/* ═══════════════════════════════════════════════════════════
   EXPORT POPOLAZIONE VALUTABILE — UC-MF-06 (28/09)
   Il file contiene esattamente le righe della tabella, con i filtri
   applicati (vista per origine, ricerca, punteggio minimo) e l'ordinamento
   corrente, tutte le pagine. Prima l'export chiamava
   /api/multifitting/export, che conosce solo il pool notturno: mancavano le
   candidature dirette e il filtro di origine era ignorato.
═══════════════════════════════════════════════════════════ */
const EXPORT_COLONNE = [
  ['posizione', 'Posizione'],
  ['candidate_name', 'Candidato'],
  ['hybrid_score', 'Punteggio'],
  ['fitting', 'Fitting'],
  ['localita', 'Regione'],
  ['origine', 'Origine'],
  ['stato', 'Stato risorsa'],
  ['voto_hr', 'Voto HR'],
  ['disponibile_opportunita', 'Disponibile a nuove opportunità'],
  ['tipo_rapporto', 'Rapporto preferito'],
  ['sede', 'Sede'],
  ['area_geografica', 'Area geografica'],
  ['trasferte', 'Trasferte'],
  ['preavviso', 'Preavviso'],
  ['aspettativa_economica', 'Aspettativa economica'],
  ['cvid', 'Riferimento Altamira'],
];

function righeExport(righe) {
  return righe.map((r, i) => ({
    posizione: i + 1,
    candidate_name: r.candidate_name || '',
    hybrid_score: r.hybrid_score ?? '',
    fitting: r.fitting || '',
    localita: r.localita || '',
    origine: r.origine === 'diretta' ? 'Candidatura diretta' : 'Multi-fitting pool',
    stato: r.stato_ciclo_vita
      ? (STATO_RISORSA_LABEL[r.stato_ciclo_vita === 'R3' ? `R3${r.sottostato?.slice(-1) || 'a'}` : r.stato_ciclo_vita] || r.stato_ciclo_vita)
      : '',
    voto_hr: r.voto_hr || '',
    ...Object.fromEntries(CONDIZIONI_SA.map((k) => [k, etichettaCondizione(k, r[k])])),
    cvid: r.cvid || '',
  }));
}

function scarica(contenuto, tipo, nomeFile) {
  const blob = new Blob([contenuto], { type: tipo });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeFile;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function ExportLonglistMenu({ righe, annuncio, filtri }) {
  const [open, setOpen] = useState(false);
  const [messaggio, setMessaggio] = useState(null);

  function doExport(formato) {
    setOpen(false);
    setMessaggio(null);
    try {
      const dati = righeExport(righe);
      const base = `longlist_${String(annuncio?.id || 'annuncio').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}`;
      if (formato === 'csv') {
        // Separatore «;» e BOM: è ciò che Excel in italiano apre correttamente.
        const cella = (v) => {
          const t = String(v ?? '');
          return /[";\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
        };
        const righeCsv = [
          EXPORT_COLONNE.map(([, l]) => cella(l)).join(';'),
          ...dati.map((d) => EXPORT_COLONNE.map(([k]) => cella(d[k])).join(';')),
        ];
        scarica('\ufeff' + righeCsv.join('\r\n'), 'text/csv;charset=utf-8', `${base}.csv`);
      } else {
        const corpo = {
          annuncio: { id: annuncio?.id, titolo: annuncio?.title },
          filtri,
          esportato_il: new Date().toISOString(),
          righe: dati,
        };
        scarica(JSON.stringify(corpo, null, 2), 'application/json', `${base}.json`);
      }
      setMessaggio(`File generato: ${dati.length} ${dati.length === 1 ? 'riga' : 'righe'}.`);
    } catch (err) {
      // UC-MF-06, flusso alternativo 1: nessun file, schermata e filtri invariati.
      setMessaggio("Errore nella generazione del file. Riprova l'operazione.");
    }
  }

  return (
    <div style={{ position:'relative', display:'flex', alignItems:'center', gap:8 }}>
      {messaggio && <span style={{ fontSize:11.5, color:T.txt3 }}>{messaggio}</span>}
      <button onClick={() => setOpen(o => !o)} disabled={righe.length === 0}
        title={righe.length === 0 ? 'Nessuna riga da esportare' : undefined}
        style={{ height:32, padding:'0 12px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, fontSize:13, cursor: righe.length === 0 ? 'default' : 'pointer', fontFamily:T.font, display:'flex', alignItems:'center', gap:6 }}>
        ⬇ Esporta <span style={{ fontSize:10 }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:40 }} />
          <div style={{ position:'absolute', right:0, top:36, zIndex:41, background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:8, boxShadow:'0 6px 18px rgba(0,0,0,0.10)', padding:4, minWidth:200 }}>
            {[['csv', '📄 CSV (Excel)'], ['json', '🔧 JSON']].map(([fmt, lbl]) => (
              <button key={fmt} onClick={() => doExport(fmt)}
                style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 10px', border:'none', background:'none', fontSize:13, cursor:'pointer', fontFamily:T.font, borderRadius:6 }}>
                {lbl}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   EXPORT MENU
   Bottone unico con dropdown formato (xlsx/csv/json). Chiama
   /api/multifitting/export con i filtri della vista corrente
   (passati via buildParams) e scarica il blob risultante.
═══════════════════════════════════════════════════════════ */
function ExportMenu({ buildParams }) {
  const [open, setOpen]             = useState(false);
  const [downloading, setDownloading] = useState(false);

  async function doExport(format) {
    setOpen(false);
    setDownloading(true);
    try {
      const params = new URLSearchParams({ format });
      const raw = buildParams() || {};
      Object.entries(raw).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') params.set(k, v);
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/multifitting/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Errore export (HTTP ${response.status})`);

      const blob = await response.blob();
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `multifitting_export.${format}`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Errore durante l'export: " + err.message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div style={{ position:'relative' }}>
      <button onClick={() => setOpen(o => !o)} disabled={downloading}
        style={{ height:32, padding:'0 12px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, fontSize:13, cursor: downloading ? 'default' : 'pointer', fontFamily:T.font, display:'flex', alignItems:'center', gap:6 }}>
        {downloading ? '⏳ Esportazione…' : <>⬇ Esporta <span style={{ fontSize:10 }}>▾</span></>}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:300 }} />
          <div style={{ position:'absolute', top:36, right:0, zIndex:301, background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', minWidth:150, overflow:'hidden' }}>
            {[['xlsx', '📊 Excel (.xlsx)'], ['csv', '📄 CSV'], ['jsonl', '🔧 JSONL']].map(([fmt, lbl]) => (
              <div key={fmt} onClick={() => doExport(fmt)}
                style={{ padding:'9px 14px', fontSize:13, cursor:'pointer', fontFamily:T.font, whiteSpace:'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >{lbl}</div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const PAGE_SIZE = 15;

/* ═══════════════════════════════════════════════════════════
   SIDE PANEL
═══════════════════════════════════════════════════════════ */
function SidePanel({ candidate, allRows, onClose, onResourceUpdated }) {
  const crossFit = allRows
    .filter(r => r.cvid === candidate.cvid)
    .sort((a, b) => b.hybrid_score - a.hybrid_score);

  const reqPresent = Array.isArray(candidate.req_present) ? candidate.req_present : [];
  const reqMissing = Array.isArray(candidate.req_missing) ? candidate.req_missing : [];
  // UAT-06: senza ragionamento né requisiti non esiste un'analisi da mostrare.
  // «Nessuno — fit completo» ha senso solo se l'analisi c'è (altrimenti
  // l'assenza di dati verrebbe letta come fit completo).
  const hasAnalisi = !!candidate.reasoning || reqPresent.length > 0 || reqMissing.length > 0;
  // Candidato senza punteggio su QUESTO annuncio (es. candidatura diretta su un
  // annuncio non valutato dal run): niente barra, LLM, affinità né badge «Storico».
  const hasScore = candidate.hybrid_score != null;

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:200 }} />
      <div style={{
        position:'fixed', top:0, right:0, bottom:0, width:400, zIndex:201,
        background:T.surface, overflowY:'auto',
        boxShadow:'-4px 0 24px rgba(0,0,0,0.12)',
        fontFamily:T.font, fontSize:14
      }}>
        <div style={{ padding:'16px 18px', borderBottom:`0.5px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:T.surface, zIndex:1 }}>
          <h3 style={{ fontSize:14, fontWeight:600 }}>Dettaglio risorsa</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:T.txt2, fontSize:18, lineHeight:1 }}>✕</button>
        </div>

        <div style={{ paddingBottom:32 }}>
          {/* Identity */}
          <div style={{ padding:'16px 18px', borderBottom:`0.5px solid ${T.border}`, display:'flex', alignItems:'center', gap:10 }}>
            <Avatar name={candidate.candidate_name} size={40} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:600 }}>{candidate.candidate_name}</div>
              <div style={{ fontSize:12, color:T.txt2 }}>{candidate.competenza_principale || '—'}</div>
            </div>
            {/* Bug Log 06/10 (3.1.2): CV reale in nuova scheda, stesso endpoint di Gestione candidato */}
            <ResourceCvButton cvid={candidate.cvid} />
          </div>

          {/* FE-01 / SIT-022 (15/10): contenuto unico del dettaglio risorsa
              (ResourceDettaglio), lo stesso della Console HR. Qui resta solo
              il blocco di contesto dell'annuncio: punteggio, ragionamento,
              requisiti e fitting sugli altri annunci del run. Contatti,
              competenze, costi, Self-Assessment, esperienze, stato e
              anagrafica sono nella parte fissa del componente, nello stesso
              ordine da ogni ingresso (UC-CD-01). */}
          <ResourceDettaglio
            resourceId={candidate.cvid}
            jdId={candidate.target_job_id}
            riga={candidate}
            statoCicloVita={candidate.stato_ciclo_vita}
            onResourceUpdated={onResourceUpdated}
            bloccoContesto={(
              <div style={{ background:'#F6FAFE', borderBottom:`0.5px solid ${T.border}` }}>
                <div style={{ padding:'10px 18px 0', fontSize:10.5, fontWeight:700, color:T.accentTxt, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  Fitting sull'annuncio
                </div>
              {/* Score */}
              <div style={{ padding:'14px 18px', borderBottom:`0.5px solid ${T.border}` }}>
                {hasScore ? (
                  <>
                    <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:7 }}>
                      <span style={{ fontSize:32, fontWeight:700, lineHeight:1 }}>{candidate.hybrid_score}</span>
                      <div>
                        <div style={{ fontSize:11, color:T.txt3 }}>Hybrid Score</div>
                        <FittingBadge fitting={candidate.fitting} />
                      </div>
                    </div>
                    <ScoreFill score={candidate.hybrid_score} />
                    <div style={{ display:'flex', gap:12, marginTop:8, fontSize:12, color:T.txt2 }}>
                      <span>LLM: <strong>{candidate.llm_score}</strong></span>
                      <span>Affinità semantica: <strong>{candidate.cosine_norm}</strong></span>
                      <StatusBadge status={candidate.status || 'REUSED'} />
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:7 }}>
                      <span style={{ fontSize:32, fontWeight:700, lineHeight:1, color:T.txt3 }}>—</span>
                      <div style={{ fontSize:11, color:T.txt3 }}>Hybrid Score</div>
                    </div>
                    <div style={{ fontSize:12, color:T.txt2 }}>
                      Nessun punteggio calcolato su questo annuncio.
                      {crossFit.length > 0 ? ' Il fitting sugli altri annunci del run è riportato più sotto.' : ''}
                    </div>
                  </>
                )}
              </div>

              {/* Reasoning */}
              {candidate.reasoning && (
                <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
                  <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>🧠 Ragionamento AI</div>
                  <div style={{ fontSize:12, lineHeight:1.6, color:T.txt2 }}>{candidate.reasoning}</div>
                </div>
              )}

              {/* Requisiti presenti */}
              {reqPresent.length > 0 && (
                <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
                  <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Requisiti presenti</div>
                  {reqPresent.map((r, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, marginBottom:4 }}>
                      <span style={{ color:T.greenTxt }}>✓</span><span>{r}</span>
                    </div>
                  ))}
                </div>
              )}

              {hasScore && !hasAnalisi && (
                <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}`, fontSize:12, color:T.txt3 }}>
                  Analisi non disponibile: ragionamento e requisiti non sono registrati per questo punteggio.
                  {candidate.status === 'REUSED' ? ' Il punteggio è stato riutilizzato da un run precedente.' : ''}
                </div>
              )}

              {/* Requisiti assenti */}
              {hasScore && hasAnalisi && (
              <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
                <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Requisiti assenti</div>
                {reqMissing.length > 0
                  ? reqMissing.map((r, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, marginBottom:4 }}>
                        <span style={{ color:T.redTxt }}>✗</span><span>{r}</span>
                      </div>
                    ))
                  : <span style={{ fontSize:12, color:T.txt3 }}>Nessuno — fit completo</span>
                }
              </div>
              )}

              {/* Cross-fit */}
              {(crossFit.length > 0 || candidate.source_job_name) && (
              <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
                <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>
                  📈 Fitting su tutti gli annunci del run
                </div>
                {candidate.source_job_name && (
                  <div style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 10px', borderRadius:6, background:T.greenBg, marginBottom:12 }}>
                    <span style={{ fontSize:13 }}>📌</span>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color:T.greenTxt, textTransform:'uppercase', letterSpacing:'0.05em' }}>Candidatura originale</div>
                      <div style={{ fontSize:12, fontWeight:500, color:T.greenTxt, marginTop:1 }}>{candidate.source_job_name}</div>
                    </div>
                  </div>
                )}
                {crossFit.map((r, i) => {
                  const isCurrent = r.target_job_id === candidate.target_job_id;
                  const barColor = r.hybrid_score >= 75 ? T.accent : r.hybrid_score >= 50 ? '#F59E0B' : '#EF4444';
                  return (
                    <div key={i} style={{ marginBottom:7, padding:'6px 8px', borderRadius:6, background: isCurrent ? T.accentBg : 'transparent' }}>
                      <div style={{ fontSize:11, color: isCurrent ? T.accentTxt : T.txt2, fontWeight: isCurrent ? 600 : 400, marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {isCurrent ? '▶ ' : ''}{r.target_job_name}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ flex:1, height:4, borderRadius:2, background:T.surface2 }}>
                          <div style={{ width:`${r.hybrid_score}%`, height:'100%', borderRadius:2, background: isCurrent ? T.accent : barColor }} />
                        </div>
                        <span style={{ fontSize:11, fontWeight:600, minWidth:22, color:T.txt2 }}>{r.hybrid_score}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}

              </div>
            )}
          />
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCREEN: Cluster (raggruppamento per profilo professionale del CANDIDATO)
═══════════════════════════════════════════════════════════ */
function ScreenCluster({ rows, consipProfiles, onSelectCluster }) {
  const [search, setSearch] = useState('');
  const [sort, setSort]     = useState('cand_desc');

  // Distingue "il backend non manda ancora il campo" (key assente su TUTTE le righe,
  // problema di integrazione) da "il candidato non ha compilato il profilo" (valore
  // vuoto/null su alcune righe, ~33% atteso — vedi audit su data_sources, non è un bug).
  const fieldNotYetShipped = rows.length > 0 && rows.every(r => r[CLUSTER_FIELD] === undefined);

  // Dedupe per candidato: un cvid può comparire su più annunci nello stesso run.
  // Il profilo professionale è un attributo del candidato (stabile), quindi si tiene
  // una sola riga per cvid, con lo score migliore ottenuto nel run.
  const bestByCvid = {};
  rows.forEach(r => {
    const prev = bestByCvid[r.cvid];
    if (!prev || (r.hybrid_score ?? 0) > (prev.hybrid_score ?? 0)) bestByCvid[r.cvid] = r;
  });

  const byCluster = {};
  Object.values(bestByCvid).forEach(r => {
    const raw = r[CLUSTER_FIELD];
    const key = raw && raw.trim() ? raw : CLUSTER_UNSET_LABEL;
    if (!byCluster[key]) byCluster[key] = { id: key, title: key, cands: new Set(), scores: [], minRunDate: r.run_date, maxRunDate: r.run_date };
    byCluster[key].cands.add(r.cvid);
    if (typeof r.hybrid_score === 'number') byCluster[key].scores.push(r.hybrid_score);
    if (r.run_date && (!byCluster[key].minRunDate || r.run_date < byCluster[key].minRunDate)) byCluster[key].minRunDate = r.run_date;
    if (r.run_date && (!byCluster[key].maxRunDate || r.run_date > byCluster[key].maxRunDate)) byCluster[key].maxRunDate = r.run_date;
  });

  let clusters = Object.values(byCluster).map(c => ({
    ...c,
    candCount: c.cands.size,
    scoreAvg: c.scores.length ? Math.round(c.scores.reduce((a, b) => a + b, 0) / c.scores.length) : 0,
  }));
  if (search) clusters = clusters.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));
  if (sort === 'cand_desc')  clusters.sort((a, b) => b.candCount - a.candCount);
  if (sort === 'score_desc') clusters.sort((a, b) => b.scoreAvg - a.scoreAvg);
  if (sort === 'title_asc')  clusters.sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:600, letterSpacing:'-0.02em' }}>Cluster</h1>
        <p style={{ fontSize:13, color:T.txt2, marginTop:3 }}>Candidati raggruppati per profilo professionale.</p>
      </div>

      {fieldNotYetShipped && (
        <div style={{ padding:'12px 16px', borderRadius:10, background:T.amberBg, color:T.amberTxt, fontSize:13, marginBottom:16 }}>
          ⚠️ Il campo <code>{CLUSTER_FIELD}</code> non è ancora presente nel payload di <code>/api/multifitting/nightly</code>.
          Verificare che la vista DB e <code>multifitting_api.py</code> siano stati aggiornati (vedi nota CLUSTER CONFIG in cima al file).
        </div>
      )}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
          <input type="text" placeholder="Cerca cluster…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:T.txt2 }}>Ordina per</span>
          <select value={sort} onChange={e => setSort(e.target.value)}
            style={{ height:32, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, background:T.surface, fontFamily:T.font }}>
            <option value="cand_desc">N. candidati ↓</option>
            <option value="score_desc">Score medio ↓</option>
            <option value="title_asc">Titolo A→Z</option>
          </select>
        </div>
      </div>

      <div style={{ fontSize:12, color:T.txt3, marginBottom:12 }}>{clusters.length} di {Object.keys(byCluster).length} cluster</div>

      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {clusters.map(c => {
          const isUnset = c.id === CLUSTER_UNSET_LABEL;
          return (
            <div key={c.id} onClick={() => onSelectCluster(c)}
              style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', cursor:'pointer', transition:'border-color 0.13s, box-shadow 0.13s', opacity: isUnset ? 0.75 : 1 }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderMd; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:38, height:38, borderRadius:6, background: isUnset ? T.grayBg : T.accentBg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ fontSize:16 }}>{isUnset ? '❔' : '🗂️'}</span>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:600, marginBottom:3, fontStyle: isUnset ? 'italic' : 'normal', color: isUnset ? T.txt2 : T.txt }}>{c.title}</div>
                  <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500, background: isUnset ? T.grayBg : T.accentBg, color: isUnset ? T.grayTxt : T.accentTxt }}>Score medio: {c.scoreAvg}</span>
                  {(() => {
                    const mapping = CONSIP_SLUG_MAP[c.id];
                    const profile = mapping && consipProfiles.find(p => p.profile_slug === mapping.slug);
                    if (profile) {
                      return (
                        <div style={{ fontSize:12, color:T.txt2, marginTop:6, lineHeight:1.4, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                          {profile.profile_text}
                        </div>
                      );
                    }
                    if (isUnset) {
                      return (
                        <div style={{ fontSize:12, color:T.txt3, marginTop:6, fontStyle:'italic' }}>
                          Campo "Profilo professionale" non compilato su Altamira — non è un errore di sistema
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', flexShrink:0, marginLeft:24 }}>
                  <span style={{ fontSize:28, fontWeight:700, lineHeight:1 }}>{c.candCount}</span>
                  <span style={{ fontSize:11, color:T.txt3, marginTop:2 }}>candidati</span>
                </div>
              </div>
            </div>
          );
        })}
        {clusters.length === 0 && <div style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>Nessun cluster trovato</div>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCREEN: Candidati per Cluster
   Un candidato può comparire su più annunci dello stesso cluster:
   si mostra una sola riga per candidato, con lo score migliore
   ottenuto all'interno del cluster. Il dettaglio (SidePanel) mostra
   comunque il fitting su tutti gli annunci del run.
═══════════════════════════════════════════════════════════ */
function ScreenCandidatiCluster({ rows, cluster, startDate, endDate, consipProfiles, onBack, onOpenPanel }) {
  const [search, setSearch]       = useState('');
  const [minScore, setMinScore]   = useState('');
  const [sortField, setSortField] = useState('hybrid_score');
  const [sortDir, setSortDir]     = useState('desc');
  const [page, setPage]           = useState(1);
  const [consipExpanded, setConsipExpanded] = useState(false);

  const clusterRows = rows.filter(r => {
    const raw = r[CLUSTER_FIELD];
    const key = raw && raw.trim() ? raw : CLUSTER_UNSET_LABEL;
    return key === cluster.id;
  });

  // Dedupe per candidato: tiene la riga con lo score più alto nel cluster
  const bestByCand = {};
  clusterRows.forEach(r => {
    const prev = bestByCand[r.cvid];
    if (!prev || (r.hybrid_score ?? 0) > (prev.hybrid_score ?? 0)) bestByCand[r.cvid] = r;
  });
  const annRows = Object.values(bestByCand);

  const scores  = annRows.map(r => r.hybrid_score).filter(Boolean);
  const scoreAvg = scores.length ? Math.round(scores.reduce((a,b) => a+b, 0) / scores.length) : 0;
  const scoreMax = scores.length ? Math.max(...scores) : 0;
  const topFitting = bestFitting(annRows);

  let filtered = [...annRows];
  if (search) filtered = filtered.filter(r =>
    (r.candidate_name||'').toLowerCase().includes(search.toLowerCase()) ||
    (r.localita||'').toLowerCase().includes(search.toLowerCase())
  );
  if (minScore) filtered = filtered.filter(r => r.hybrid_score >= parseInt(minScore));
  filtered.sort((a, b) => {
    const va = a[sortField] ?? '', vb = b[sortField] ?? '';
    return sortDir === 'desc' ? (vb > va ? 1 : -1) : (va > vb ? 1 : -1);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRows   = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.txt3, marginBottom:18 }}>
        <span onClick={onBack} style={{ color:T.accent, cursor:'pointer' }}>← Tutti i cluster</span>
        <span>›</span>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:400 }}>{cluster.title}</span>
      </div>

      <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', marginBottom:16 }}>
        <h2 style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>{cluster.title}</h2>
        <div style={{ fontSize:12, color:T.txt3 }}>Run: {formatDateRange(cluster.minRunDate, cluster.maxRunDate)}</div>
        {(() => {
          const mapping = CONSIP_SLUG_MAP[cluster.id];
          const profile = mapping && consipProfiles.find(p => p.profile_slug === mapping.slug);
          if (profile) {
            return (
              <div style={{ marginTop:12, paddingTop:12, borderTop:`0.5px solid ${T.border}` }}>
                <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>
                  📋 Profilo Consip: {profile.profile_titolo}
                </div>
                <div style={{
                  fontSize:13, color:T.txt2, lineHeight:1.6, whiteSpace:'pre-wrap',
                  maxHeight: consipExpanded ? 340 : 90,
                  overflowY: consipExpanded ? 'auto' : 'hidden',
                  paddingRight: consipExpanded ? 8 : 0,
                }}>
                  {profile.profile_text}
                </div>
                <button onClick={() => setConsipExpanded(v => !v)}
                  style={{ marginTop:6, padding:0, border:'none', background:'none', color:T.accent, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:T.font }}>
                  {consipExpanded ? '▲ Mostra meno' : '▼ Mostra descrizione completa'}
                </button>
                <div style={{ fontSize:10, color:T.txt3, marginTop:8 }}>Fonte: tabella consip_profile_embeddings</div>
              </div>
            );
          }
          if (cluster.id === CLUSTER_UNSET_LABEL) {
            return (
              <div style={{ marginTop:12, paddingTop:12, borderTop:`0.5px solid ${T.border}`, display:'flex', alignItems:'flex-start', gap:8, fontSize:13, color:T.txt2 }}>
                <span style={{ fontSize:14, flexShrink:0 }}>ℹ️</span>
                <span>Il campo <strong>"Profilo professionale"</strong> non è compilato su Altamira per questi candidati (schede storiche o incomplete). Non è un errore della dashboard né della pipeline di scoring.</span>
              </div>
            );
          }
          return null;
        })()}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:16 }}>
        {[['Candidati nel cluster', cluster.candCount], ['Score medio', scoreAvg], ['Score massimo', scoreMax], ['Miglior fitting', topFitting]].map(([lbl, val]) => (
          <div key={lbl} style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:10, padding:'12px 14px' }}>
            <div style={{ fontSize:22, fontWeight:600, lineHeight:1.1 }}>{val}</div>
            <div style={{ fontSize:11, color:T.txt3, marginTop:3 }}>{lbl}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
          <input type="text" placeholder="Cerca candidato…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:T.txt2 }}>Score min</span>
          <input type="number" min="0" max="100" placeholder="0" value={minScore} onChange={e => { setMinScore(e.target.value); setPage(1); }}
            style={{ height:32, width:64, padding:'0 8px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, outline:'none' }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:12, color:T.txt3 }}>{filtered.length} {filtered.length === 1 ? 'candidato' : 'candidati'} valutati</span>
          <ExportMenu buildParams={() => ({
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            cluster: cluster.id === CLUSTER_UNSET_LABEL ? '__UNSET__' : cluster.id,
            min_score: minScore || undefined,
            search: search || undefined,
          })} />
        </div>
      </div>

      <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:T.surface2 }}>
              {[['#','',36],['Candidato','candidate_name',null],['Score','hybrid_score',null],['Fitting','fitting',null],['Regione','localita',null],['Stato','status',null],['','',40]].map(([h,f,w]) => (
                <th key={h} onClick={() => f && toggleSort(f)}
                  style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:600, color: sortField===f ? T.accent : T.txt2, borderBottom:`0.5px solid ${T.border}`, whiteSpace:'nowrap', cursor:f?'pointer':'default', userSelect:'none', width:w||undefined, letterSpacing:'0.03em' }}>
                  {h}{sortField===f ? (sortDir==='desc' ? ' ↓' : ' ↑') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr><td colSpan={7} style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>Nessun candidato trovato</td></tr>
            )}
            {pageRows.map((r, i) => {
              const globalRank = (page-1)*PAGE_SIZE + i + 1;
              return (
                <tr key={i} onClick={() => onOpenPanel(r)}
                  style={{ borderBottom:`0.5px solid ${T.border}`, cursor:'pointer', transition:'background 0.1s', background: r.status === 'ERROR' ? '#fff9f9' : T.surface }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = r.status === 'ERROR' ? '#fff9f9' : T.surface}
                >
                  <td style={{ padding:'11px 12px' }}><Rank n={globalRank} /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar name={r.candidate_name} />
                      <span style={{ fontWeight:500 }}>{r.candidate_name}</span>
                    </div>
                  </td>
                  <td style={{ padding:'11px 12px', minWidth:120 }}><ScoreFill score={r.hybrid_score} /></td>
                  <td style={{ padding:'11px 12px' }}><FittingBadge fitting={r.fitting} /></td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:T.txt2 }}>{r.localita || '—'}</td>
                  <td style={{ padding:'11px 12px' }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <button onClick={e => { e.stopPropagation(); onOpenPanel(r); }}
                      style={{ height:26, padding:'0 8px', fontSize:11, border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor:'pointer', fontFamily:T.font }}>👁</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div style={{ padding:'10px 16px', borderTop:`0.5px solid ${T.border}`, display:'flex', alignItems:'center', gap:6, justifyContent:'center' }}>
            <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
              style={{ height:28, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor: page===1?'not-allowed':'pointer', opacity: page===1?0.5:1, fontFamily:T.font }}>‹</button>
            <span style={{ fontSize:12, color:T.txt2 }}>Pag. {page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
              style={{ height:28, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor: page===totalPages?'not-allowed':'pointer', opacity: page===totalPages?0.5:1, fontFamily:T.font }}>›</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCREEN: Annunci
═══════════════════════════════════════════════════════════ */
function ScreenAnnunci({ rows, onSelectAnn }) {
  const [search, setSearch] = useState('');
  const [sort, setSort]     = useState('cand_desc');

  // UAT-01 (09/10): il «numero di candidati» della card è la longlist
  // dell'annuncio (D1: candidature dirette + R3 con punteggio), la stessa
  // popolazione del dettaglio, con i Proponibili a parte (UC-MF-02 passo 6).
  // Prima contava i CV valutati dal run (6.703 per annuncio, tutto il
  // bacino) e il numero non tornava con il dettaglio. null = non ancora
  // caricati o non disponibili: la card mostra «—», non un numero sbagliato.
  const kmClient = useRealKmClient();
  const [conteggi, setConteggi] = useState(null);
  useEffect(() => {
    let annullato = false;
    kmClient.get('/hr/longlist-conteggi')
      .then((data) => {
        if (annullato) return;
        const mappa = {};
        (data?.annunci || []).forEach((a) => { mappa[String(a.jd_id)] = a; });
        setConteggi(mappa);
      })
      .catch(() => { if (!annullato) setConteggi(null); });
    return () => { annullato = true; };
  }, [kmClient]);

  const byJob = {};
  rows.forEach(r => {
    if (!byJob[r.target_job_id]) {
      byJob[r.target_job_id] = { id: r.target_job_id, title: r.target_job_name, description: r.target_job_description, cands: new Set(), minRunDate: r.run_date, maxRunDate: r.run_date };
    }
    byJob[r.target_job_id].cands.add(r.cvid);
    if (r.run_date && (!byJob[r.target_job_id].minRunDate || r.run_date < byJob[r.target_job_id].minRunDate)) byJob[r.target_job_id].minRunDate = r.run_date;
    if (r.run_date && (!byJob[r.target_job_id].maxRunDate || r.run_date > byJob[r.target_job_id].maxRunDate)) byJob[r.target_job_id].maxRunDate = r.run_date;
  });

  // Job che ricevono candidature dirette reali ma non sono mai state scelte
  // come target di cross-fit da nessun candidato del periodo: senza questo
  // passaggio sparirebbero del tutto dall'elenco, pur essendo annunci reali
  // e pubblicati (es. "Gartner - Junior AI Engineer": 242 candidature dirette,
  // 0 volte target). Restano con candCount=0 nel pool, badge esplicito in card.
  const targetIds = new Set(Object.keys(byJob));
  rows.forEach(r => {
    if (!r.source_job_id || targetIds.has(r.source_job_id)) return;
    if (!byJob[r.source_job_id]) {
      byJob[r.source_job_id] = {
        id: r.source_job_id, title: r.source_job_name || r.source_job_id,
        description: undefined, cands: new Set(),
        minRunDate: r.run_date, maxRunDate: r.run_date, noCrossFit: true,
        // Dai dati del run: false = annuncio non più aperto su Altamira
        // (coincide con il 404 «non più pubblicato» della longlist).
        chiuso: r.source_job_published === false,
      };
    }
    const j = byJob[r.source_job_id];
    if (r.run_date && (!j.minRunDate || r.run_date < j.minRunDate)) j.minRunDate = r.run_date;
    if (r.run_date && (!j.maxRunDate || r.run_date > j.maxRunDate)) j.maxRunDate = r.run_date;
  });

  // UC-MF-01: si elencano gli annunci attivi. Un annuncio chiuso su
  // Altamira non ha longlist consultabile (404 «non più pubblicato»): la
  // card portava a una lista vuota (UAT-01, segnalazione in arancione).
  let jobs = Object.values(byJob)
    .filter(j => !j.chiuso)
    .map(j => {
      const c = conteggi ? (conteggi[String(j.id)] || { totale: 0, proponibili: 0 }) : null;
      return { ...j, candCount: c ? Number(c.totale) : null, proponibiliCount: c ? Number(c.proponibili) : null };
    });
  if (search) jobs = jobs.filter(j => j.title.toLowerCase().includes(search.toLowerCase()));
  if (sort === 'cand_desc') jobs.sort((a,b) => (b.candCount ?? -1) - (a.candCount ?? -1));
  if (sort === 'title_asc') jobs.sort((a,b) => a.title.localeCompare(b.title));

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:600, letterSpacing:'-0.02em' }}>Annunci</h1>
        <p style={{ fontSize:13, color:T.txt2, marginTop:3 }}>Job description attive nel run — fonte: Altamira HRM</p>
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
          <input type="text" placeholder="Cerca annuncio…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:T.txt2 }}>Ordina per</span>
          <select value={sort} onChange={e => setSort(e.target.value)}
            style={{ height:32, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, background:T.surface, fontFamily:T.font }}>
            <option value="cand_desc">N. candidati ↓</option>
            <option value="title_asc">Titolo A→Z</option>
          </select>
        </div>
      </div>

      <div style={{ fontSize:12, color:T.txt3, marginBottom:12 }}>{jobs.length} di {Object.values(byJob).filter(j => !j.chiuso).length} annunci</div>

      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {jobs.map(jd => (
          <div key={jd.id} onClick={() => onSelectAnn(jd)}
            style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', cursor:'pointer', transition:'border-color 0.13s, box-shadow 0.13s', opacity: jd.noCrossFit ? 0.85 : 1 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderMd; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:38, height:38, borderRadius:6, background: jd.noCrossFit ? T.grayBg : T.accentBg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <span style={{ fontSize:16 }}>{jd.noCrossFit ? '📥' : '💼'}</span>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, marginBottom:3 }}>{jd.title}</div>
                <div style={{ fontSize:12, color:T.txt3 }}>Run: {formatDateRange(jd.minRunDate, jd.maxRunDate)}</div>
                {jd.description && (
                  <div style={{ fontSize:12, color:T.txt2, marginTop:6, lineHeight:1.4, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                    {htmlToText(jd.description)}
                  </div>
                )}
                {jd.noCrossFit && jd.chiuso && (
                  <div title="Annuncio non più aperto su Altamira: nessuna longlist disponibile" style={{ fontSize:11, color:T.redTxt, background:T.redBg, display:'inline-block', padding:'2px 8px', borderRadius:12, marginTop:6, marginRight:6 }}>
                    Annuncio chiuso
                  </div>
                )}
                {jd.noCrossFit && (
                  <div style={{ fontSize:11, color:T.grayTxt, background:T.grayBg, display:'inline-block', padding:'2px 8px', borderRadius:12, marginTop:6 }}>
                    Nessun cross-fit disponibile — solo candidature dirette
                  </div>
                )}
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', flexShrink:0, marginLeft:24 }}>
                <span style={{ fontSize:28, fontWeight:700, lineHeight:1, color: jd.noCrossFit ? T.txt3 : T.txt }}>{jd.candCount ?? '—'}</span>
                <span style={{ fontSize:11, color:T.txt3, marginTop:2 }}>candidati</span>
                {jd.proponibiliCount != null && (
                  <span style={{ fontSize:11, color:T.txt3 }}>{jd.proponibiliCount} Proponibili</span>
                )}
              </div>
            </div>
          </div>
        ))}
        {jobs.length === 0 && <div style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>{search.trim() ? `Nessun annuncio trovato per «${search.trim()}»` : 'Nessun annuncio trovato'}</div>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCREEN: Candidati per Annuncio
═══════════════════════════════════════════════════════════ */
function ScreenCandidatiAnn({ rows, ann, startDate, endDate, onBack, onOpenPanel, refreshKey }) {
  const kmClient = useRealKmClient();
  const [search, setSearch]       = useState('');
  const [minScore, setMinScore]   = useState(60);
  const [sortField, setSortField] = useState('hybrid_score');
  const [sortDir, setSortDir]     = useState('desc');
  const [page, setPage]           = useState(1);
  const [descExpanded, setDescExpanded] = useState(false);

  const [directRows, setDirectRows]         = useState([]);
  const [directLoading, setDirectLoading]   = useState(false);

  // Aggiunto 24/09 — GET /targets/{jd}/fitting-stato (Armando), sostituisce
  // il segnale che avevamo richiesto su /jobs (endpoint orfano, mai usato).
  // fitting_ready: embedding + almeno una riga di punteggio nel motore
  // MultiFitting — distingue "scoring non pronto" da "nessun match" per il
  // singolo candidato. fitting_ready_km è l'equivalente per il motore KM.
  const [fittingStato, setFittingStato] = useState(null);

  useEffect(() => {
    if (!ann?.id) return;
    let annullato = false;
    kmClient.get(`/targets/${encodeURIComponent(ann.id)}/fitting-stato`)
      .then((data) => { if (!annullato) setFittingStato(data); })
      .catch(() => { if (!annullato) setFittingStato(null); });
    return () => { annullato = true; };
  }, [ann?.id, kmClient]);

  const [historicalRows, setHistoricalRows]     = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [historicalSearch, setHistoricalSearch]   = useState('');
  const [historicalMinScore, setHistoricalMinScore] = useState('');
  const [historicalCollapsed, setHistoricalCollapsed] = useState(true); // collassato di default

  // Selezione per Self-Assessment — CONDIVISA fra le tre tabelle (candidature
  // dirette, pool, storico): stesso set, così selezionare righe da sezioni
  // diverse per lo stesso invio funziona naturalmente, coerente con UC-MF-03
  // che le considera un'unica popolazione valutabile. Chiave: r.cvid — lo
  // stesso id già usato da ResourceSelfAssessmentPanel (resourceId=candidate.cvid).
  const [selectedSA, setSelectedSA] = useState(() => new Set());

  // selectedSA/toggleSelectedSA — usati oggi solo dalla sezione "Storico
  // proponibili" più sotto, nascosta ({false && ...}). Non raggiungibili da
  // nessuna schermata visibile. L'invio vero e proprio (handleInviaSelezionati,
  // sulla stessa selezione) è stato tolto il 29/09: era già disattivato da
  // giorni (cvid non riconosciuto da /hr/invite) e ora l'invio unificato più
  // sotto (selectedInvio, su candidato_id) copre lo stesso bisogno.
  function toggleSelectedSA(cvid) {
    setSelectedSA((prev) => {
      const next = new Set(prev);
      if (next.has(cvid)) next.delete(cvid);
      else next.add(cvid);
      return next;
    });
  }

  useEffect(() => {
    if (!ann?.id) return;
    setDirectLoading(true);
    axios.get(`/api/multifitting/direct/${ann.id}`)
      .then(r => setDirectRows(r.data || []))
      .catch(() => setDirectRows([]))
      .finally(() => setDirectLoading(false));
  }, [ann.id]);

  useEffect(() => {
    if (!ann?.id) return;
    setHistoricalLoading(true);
    axios.get(`/api/multifitting/historical/${ann.id}`)
      .then(r => setHistoricalRows(r.data || []))
      .catch(() => setHistoricalRows([]))
      .finally(() => setHistoricalLoading(false));
  }, [ann.id]);

  // soloDemo/eDemo e soloNuovi/eNuovo tolti il 26/09, su richiesta esplicita.
  // La cautela originale di soloDemo (evitare di selezionare/invitare una
  // persona vera) non serve più qui: l'invio da questa schermata non
  // esiste da giorni, resta solo dentro D1 (Console HR) — l'unico punto
  // dove quel rischio è reale, e lì il filtro non c'era comunque.

  // Filtro di origine per la tabella unificata (25/09, SAL) — 'tutte' di
  // default, così chi apre la pagina vede subito tutto insieme come
  // richiesto, senza dover scegliere prima.
  const [vistaFiltro, setVistaFiltro] = useState('tutte');

  const annRows = rows.filter(r => r.target_job_id === ann.id);
  const directRowsFiltrate = directRows;


  // Arricchimento con stato vero — 25/09, sostituisce POST /resources/stati.
  // /hr/longlist ha già stato_ciclo_vita, sottostato e invitabilità corretti
  // (risorsa_riferimento, R3 vero) — lo stesso identico dato che alimenta
  // Console HR. Le tabelle qui sopra restano sulla fonte vecchia (serve la
  // ricchezza CV — competenze, certificazioni, motivazione — che
  // /hr/longlist non ha e non deve avere, non è il suo scopo) ma ogni riga
  // viene arricchita con lo stato vero per cvid/altamira_ref, invece di
  // indovinarlo o lasciarlo assente. Le due fonti descrivono la stessa
  // realtà da due punti di vista — questa unione le fa finalmente
  // concordare, senza dover scegliere una delle due.
  // 28/09 — Popolazione valutabile dell'AF (D1 = D2 + D3, V6): la
  // COMPOSIZIONE viene da /hr/longlist (vista v_longlist_annuncio, migrazione
  // 035): candidature dirette in qualunque stato + risorse R3 con punteggio
  // MultiFitting non candidate, R4 escluse, una riga per risorsa. Prima il
  // pool veniva da /api/multifitting/nightly senza alcun filtro di stato, e
  // in tabella comparivano anche R2 e R4. Le fonti MultiFitting restano solo
  // per ARRICCHIRE ogni riga (regione, competenze, ragionamento AI…).
  const [longlistRows, setLonglistRows] = useState([]);
  const [longlistLoading, setLonglistLoading] = useState(false);
  const [longlistErrore, setLonglistErrore] = useState(null);
  const [longlistTick, setLonglistTick] = useState(0);
  // Filtri sulle condizioni dichiarate nel Self-Assessment (06/10): li
  // applica il server (/hr/longlist), '' = filtro assente. Chi non ha
  // dichiarato il campo filtrato è escluso, salvo «anche non dichiarati».
  const [filtriCondizioni, setFiltriCondizioni] = useState({ disponibile: '', sede: '', trasferte: '', tipo_rapporto: '' });
  const [ancheNonDichiarati, setAncheNonDichiarati] = useState(false);
  const filtriCondizioniAttivi = Object.values(filtriCondizioni).some(Boolean);
  // SIT-017: elenco dei criteri attivi, mostrato nel messaggio di risultato vuoto.
  const criteriAttivi = [
    search.trim() && `nominativo «${search.trim()}»`,
    vistaFiltro !== 'tutte' && (vistaFiltro === 'diretta' ? 'solo candidature dirette' : 'solo pool'),
    minScore !== '' && Number(minScore) > 0 && `punteggio minimo ${minScore}`,
    ...FILTRI_CONDIZIONI
      .filter(([param]) => filtriCondizioni[param])
      .map(([param, lbl, campo]) => `${lbl.toLowerCase()}: ${ETICHETTE_CONDIZIONI[campo]?.[filtriCondizioni[param]] || filtriCondizioni[param]}`),
  ].filter(Boolean);

  useEffect(() => {
    if (!ann?.id) return;
    let annullato = false;
    setLonglistLoading(true);
    setLonglistErrore(null);
    const queryCondizioni = {};
    Object.entries(filtriCondizioni).forEach(([k, v]) => { if (v) queryCondizioni[k] = v; });
    if (filtriCondizioniAttivi && ancheNonDichiarati) queryCondizioni.includi_non_dichiarati = true;
    kmClient.get(`/hr/longlist/${encodeURIComponent(ann.id)}`, { query: { min_score: 0, limit: 1000, ...queryCondizioni } })
      .then((data) => { if (!annullato) setLonglistRows(Array.isArray(data?.candidati) ? data.candidati : []); })
      .catch((err) => {
        if (annullato) return;
        setLonglistRows([]);
        setLonglistErrore(err?.detail || 'Popolazione valutabile non disponibile.');
      })
      .finally(() => { if (!annullato) setLonglistLoading(false); });
    return () => { annullato = true; };
  }, [ann?.id, kmClient, refreshKey, longlistTick, filtriCondizioni, filtriCondizioniAttivi, ancheNonDichiarati]);


  // Nomi "Finali" mantenuti per non toccare i riferimenti sotto (unione,
  // longlistMap) — ora coincidono semplicemente con le righe non filtrate.
  const annRowsFinali = annRows;
  const directRowsFinali = directRowsFiltrate;

  // Unificazione richiesta il 25/09 (SAL) — prima due tabelle quasi
  // identiche per struttura (stessi campi: candidate_name, hybrid_score,
  // fitting, localita), difficili da confrontare per trovare i punteggi
  // più alti. Unite qui in un'unica popolazione, con vistaFiltro a
  // restringere quando serve. Dedup per cvid: se una persona compare in
  // entrambe le fonti (si è candidata E il cross-fit l'ha valutata),
  // tiene la riga dalla candidatura diretta — più specifica per questo
  // annuncio, non un incrocio generico. Ogni riga è arricchita con lo
  // stato vero da longlistMap — stato_ciclo_vita resta undefined per chi
  // non è mai stato trovato lì (genuinamente non collegato a candidato).
  //
  // target_job_id — 27/09: le righe delle candidature dirette arrivano da
  // un'altra fonte e non lo portano. Il pannello di dettaglio lo usa come
  // annuncio di riferimento (jdId) per l'invio singolo del Self-Assessment:
  // senza, per le candidature dirette l'invio risultava "senza annuncio".
  // Siamo dentro il dettaglio di un annuncio, quindi è sempre ann.id.
  const tutteLeRighe = React.useMemo(() => {
    const dettaglio = new Map();
    for (const r of annRowsFinali) if (r.cvid) dettaglio.set(String(r.cvid), r);
    for (const r of directRowsFinali) if (r.cvid && !dettaglio.has(String(r.cvid))) dettaglio.set(String(r.cvid), r);
    return longlistRows.map((c) => {
      const d = dettaglio.get(String(c.punteggio_mf_cvid || '')) || dettaglio.get(String(c.altamira_ref || '')) || {};
      const punteggio = c.punteggio_mf != null ? Number(c.punteggio_mf) : null;
      return {
        ...d,
        cvid: d.cvid || c.punteggio_mf_cvid || c.altamira_ref || c.candidato_id,
        candidate_name: d.candidate_name || c.nome,
        hybrid_score: punteggio,
        has_score: punteggio != null,
        fitting: punteggio != null ? (c.punteggio_mf_fascia || d.fitting) : null,
        origine: c.origine === 'candidatura_diretta' ? 'diretta' : 'pool',
        stato_ciclo_vita: c.stato_ciclo_vita,
        sottostato: c.sottostato,
        invitabile: c.invitabile,
        motivo_non_invitabile: c.motivo_non_invitabile,
        candidato_id: c.candidato_id,
        profilo_sa: c.profilo_sa,
        // Aggiunti il 29/09 per l'invio unificato — stessi campi già usati
        // da HrSelfAssessmentConsole.jsx, arrivano già pronti da /hr/longlist:
        // stato_ultimo_sa (Inviato/In compilazione/Compilato/Scaduto o null),
        // ultimo_invito (created_at/consumed_at, per V32 e per la data
        // mostrata), sa_aperto (V22 — un'istanza già apribile dal candidato).
        stato_ultimo_sa: c.stato_ultimo_sa,
        ultimo_invito: c.ultimo_invito || null,
        ultimo_sollecito: c.ultimo_sollecito || null,
        sa_aperto: c.sa_aperto,
        // 06/10 — condizioni dichiarate nel Self-Assessment, dall'ultimo
        // profilo che le porta (null se mai dichiarate).
        ...Object.fromEntries(CONDIZIONI_SA.map((k) => [k, c[k] ?? null])),
        condizioni_il: c.condizioni_il || null,
        // 23/10 — azione 24: Voto HR. Da /hr/longlist quando il backend lo
        // espone (voto_cv), altrimenti dalla riga MultiFitting.
        voto_hr: c.voto_cv ?? d.voto_cv ?? null,
        target_job_id: ann.id,
      };
    });
  }, [annRowsFinali, directRowsFinali, longlistRows, ann.id]);

  const righeVistaFiltrata = vistaFiltro === 'tutte' ? tutteLeRighe : tutteLeRighe.filter(r => r.origine === vistaFiltro);


  let filtered = [...righeVistaFiltrata];
  if (search) filtered = filtered.filter(r =>
    (r.candidate_name||'').toLowerCase().includes(search.toLowerCase()) ||
    (r.localita||'').toLowerCase().includes(search.toLowerCase())
  );
  // UC-MF-03, flusso alternativo 2: una risorsa senza punteggio resta in
  // tabella (in coda), il filtro sul punteggio minimo vale solo per chi ce l'ha.
  if (minScore) filtered = filtered.filter(r => r.hybrid_score == null || r.hybrid_score >= parseInt(minScore));
  filtered.sort((a, b) => {
    if (sortField === 'hybrid_score') {
      const na = a.hybrid_score == null, nb = b.hybrid_score == null;
      if (na !== nb) return na ? 1 : -1; // senza punteggio sempre in coda
      if (na && nb) return 0;
    }
    if (sortField === 'voto_hr') {
      const ra = rangoVotoHr(a.voto_hr), rb = rangoVotoHr(b.voto_hr);
      if ((ra == null) !== (rb == null)) return ra == null ? 1 : -1; // senza voto sempre in coda
      if (ra === rb) return 0;
      // «desc» = dal voto migliore (Top) al peggiore.
      return sortDir === 'desc' ? ra - rb : rb - ra;
    }
    const va = a[sortField] ?? '', vb = b[sortField] ?? '';
    if (va === vb) return 0;
    return sortDir === 'desc' ? (vb > va ? 1 : -1) : (va > vb ? 1 : -1);
  });

  const topFitting = bestFitting(filtered);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRows   = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  // Box riassuntivi ricalcolati sulla vista filtrata (vistaFiltro), non
  // più fissi sul solo pool — così cambiano davvero scegliendo
  // "Solo candidature dirette" o "Solo pool", come richiesto il 25/09.
  const scoresFiltrati = filtered.map(r => r.hybrid_score).filter(v => v != null);
  const scoreAvg = scoresFiltrati.length ? Math.round(scoresFiltrati.reduce((a,b) => a+b, 0) / scoresFiltrati.length) : 0;
  const scoreMax = scoresFiltrati.length ? Math.max(...scoresFiltrati) : 0;

  // "Proponibili" — richiesto da UC-MF-02 (§7.1, D3): conteggio delle
  // risorse in stato R3, non il totale generico. Lo stato è già arricchito
  // su ogni riga da longlistMap (vedi tutteLeRighe sopra), non serve più
  // interrogare una mappa separata qui.
  const proponibiliCount = filtered.filter(r => r.stato_ciclo_vita === 'R3').length;

  /* ═══════════════════════════════════════════════════════════
     INVIO SELF-ASSESSMENT UNIFICATO — 29/09 (UC-SA-01/WF-MF-02)
     Portato qui da HrSelfAssessmentConsole.jsx (D1), che restava un
     pannello separato sotto la stessa identica popolazione. Stessa
     logica: selezione per candidato_id (non cvid — /hr/invite non lo
     riconosce), verifica V22 (sa_aperto/stato_ultimo_sa), anteprima
     obbligatoria (dry_run) prima della conferma. Il controllo V32 (invio
     recente) è stato tolto: era una soglia hardcoded nel client, non
     aderente all'AF — va imposta lato server (vedi nota per Armando).
  ═══════════════════════════════════════════════════════════ */
  const [selectedInvio, setSelectedInvio] = useState(() => new Set());
  const [previewInvio, setPreviewInvio] = useState(null);
  const [previewInvioLoading, setPreviewInvioLoading] = useState(false);
  const [confirmInvioLoading, setConfirmInvioLoading] = useState(false);
  const [sendInvioError, setSendInvioError] = useState(null);
  const [sendInvioSuccess, setSendInvioSuccess] = useState(null);
  // 15/10 — UC-SA-01 FA4: le righe con SA già aperto restano selezionabili e,
  // alla conferma, ricevono un sollecito (POST /hr/risorse/{id}/sollecito-sa:
  // stesso link, scadenza invariata, nessuna nuova istanza) invece di un invio.
  const [selectedSollecito, setSelectedSollecito] = useState(() => new Set());
  const [previewSollecito, setPreviewSollecito] = useState(null); // { ok: [id], esclusi: [{ nome, motivo }] }
  const totaleSelezionati = selectedInvio.size + selectedSollecito.size;
  const nomeRisorsa = (id) => {
    const c = longlistRows.find((x) => x.candidato_id === id);
    return (c && (c.nome || c.candidate_name)) || 'Risorsa';
  };

  const giaInvitato = (r) => Boolean(r.sa_aperto) || ['Inviato', 'In compilazione'].includes(r.stato_ultimo_sa);
  const invitatoDiRecente = (r) => !giaInvitato(r) && invioRecente(r.ultimo_invito?.created_at);

  function parseRispostaInvito(data) {
    if (!data || typeof data !== 'object') return { known: false, raw: data };
    const haCampiNoti = 'candidati_in_lista' in data || 'inviati' in data;
    if (!haCampiNoti) return { known: false, raw: data };
    return {
      known: true,
      candidatiInLista: data.candidati_in_lista ?? null,
      inviati: data.inviati ?? null,
      saltati: data.saltati ?? null,
      esclusi: Array.isArray(data.esclusi) ? data.esclusi : [],
      identificativiIgnoti: Array.isArray(data.identificativi_ignoti) ? data.identificativi_ignoti : [],
      destinatari: Array.isArray(data.destinatari) ? data.destinatari : [],
      raw: data,
    };
  }

  function inviteUrlUnificato(dryRun) {
    const params = new URLSearchParams({ dry_run: String(dryRun) });
    return `/hr/invite/${encodeURIComponent(ann.id)}?${params.toString()}`;
  }

  // 29/09 — canale: 'email' esplicito nel corpo di ogni chiamata a
  // inviteUrlUnificato. Decisione di business: per ora solo email, mai
  // WhatsApp. Il default lato backend è "auto" (preferisce WhatsApp se la
  // risorsa ha un numero, V20) — richiesto ad Armando di cambiarlo, ma
  // finché non è fatto lo forziamo qui.

  async function handlePreviewInvio() {
    if (totaleSelezionati === 0) return;
    setPreviewInvioLoading(true);
    setSendInvioError(null);
    setSendInvioSuccess(null);
    try {
      // Verifica dei solleciti, una chiamata per risorsa (dry_run).
      const ok = [];
      const esclusi = [];
      await Promise.all([...selectedSollecito].map(async (id) => {
        try {
          const r = await kmClient.post(`/hr/risorse/${encodeURIComponent(id)}/sollecito-sa?dry_run=true`);
          if (r?.esito === 'simulato') ok.push(id);
          else esclusi.push({ nome: nomeRisorsa(id), motivo: r?.motivo || r?.esito || 'sollecito non possibile' });
        } catch (e) {
          esclusi.push({ nome: nomeRisorsa(id), motivo: e?.detail || 'errore di verifica' });
        }
      }));
      setPreviewSollecito({ ok, esclusi });
      const result = selectedInvio.size > 0
        ? await kmClient.post(inviteUrlUnificato(true), { resource_ids: [...selectedInvio], canale: 'email' })
        : { candidati_in_lista: 0, inviati: 0, saltati: 0, esclusi: [] };
      setPreviewInvio(parseRispostaInvito(result));
    } catch (err) {
      setSendInvioError(err?.detail || "Errore durante l'anteprima dell'invio.");
      setPreviewInvio(null);
    } finally {
      setPreviewInvioLoading(false);
    }
  }

  async function handleConfirmInvio() {
    if (totaleSelezionati === 0) return;
    setConfirmInvioLoading(true);
    setSendInvioError(null);
    const idsInviati = [...selectedInvio];
    const idsSollecito = previewSollecito?.ok || [];
    try {
      const result = idsInviati.length > 0
        ? await kmClient.post(inviteUrlUnificato(false), { resource_ids: idsInviati, canale: 'email' })
        : { candidati_in_lista: 0, inviati: 0, saltati: 0, esclusi: [] };
      let solleciti = 0;
      const sollecitiFalliti = [];
      for (const id of idsSollecito) {
        try {
          const r = await kmClient.post(`/hr/risorse/${encodeURIComponent(id)}/sollecito-sa?dry_run=false`);
          if (r?.esito === 'inviato') solleciti += 1;
          else sollecitiFalliti.push({ nome: nomeRisorsa(id), motivo: r?.motivo || r?.esito || 'non inviato' });
        } catch (e) {
          sollecitiFalliti.push({ nome: nomeRisorsa(id), motivo: e?.detail || 'errore' });
        }
      }
      setSendInvioSuccess({ ...parseRispostaInvito(result), solleciti, sollecitiFalliti });
      setPreviewInvio(null);
      setPreviewSollecito(null);
      setSelectedInvio(new Set());
      setSelectedSollecito(new Set());
      // Aggiornamento immediato (stessa tecnica di HrSelfAssessmentConsole):
      // stima provvisoria, sostituita dal dato vero al prossimo giro di
      // /hr/longlist (longlistTick/refreshKey).
      const adesso = new Date().toISOString();
      setLonglistRows((prev) => prev.map((r) => (
        idsInviati.includes(r.candidato_id)
          ? { ...r, stato_ultimo_sa: 'Inviato', ultimo_invito: { stato: 'in_attesa', created_at: adesso, consumed_at: null, ...r.ultimo_invito } }
          : r
      )));
      setLonglistTick((t) => t + 1);
    } catch (err) {
      setSendInvioError(err?.detail || "Errore durante l'invio.");
    } finally {
      setConfirmInvioLoading(false);
    }
  }

  let historicalFiltered = [...historicalRows];
  if (historicalSearch) historicalFiltered = historicalFiltered.filter(r =>
    (r.candidate_name||'').toLowerCase().includes(historicalSearch.toLowerCase()) ||
    (r.localita||'').toLowerCase().includes(historicalSearch.toLowerCase())
  );
  if (historicalMinScore) historicalFiltered = historicalFiltered.filter(r => (r.hybrid_score ?? 0) >= parseInt(historicalMinScore));

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.txt3, marginBottom:18 }}>
        <span onClick={onBack} style={{ color:T.accent, cursor:'pointer' }}>← Tutti gli annunci</span>
        <span>›</span>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:400 }}>{ann.title}</span>
      </div>

      <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', marginBottom:16 }}>
        <h2 style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>{ann.title}</h2>
        <div style={{ fontSize:12, color:T.txt3 }}>Run: {formatDateRange(ann.minRunDate, ann.maxRunDate)}</div>
        {ann.description && (
          <div style={{ marginTop:12, paddingTop:12, borderTop:`0.5px solid ${T.border}` }}>
            {/* Richiesto il 26/09 — prima l'anteprima (90px, poche righe)
                restava sempre visibile anche a chiuso, con solo l'altezza
                che cambiava. Ora, a chiuso, la descrizione non si vede
                affatto: resta solo il bottone. Si apre per intero solo
                al click, non prima. */}
            {descExpanded && (
              <div
                style={{
                  fontSize:13, color:T.txt2, lineHeight:1.6,
                  maxHeight:340, overflowY:'auto', overflowX:'hidden', paddingRight:8,
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeJobHtml(ann.description) }}
              />
            )}
            <button onClick={() => setDescExpanded(v => !v)}
              style={{ marginTop: descExpanded ? 6 : 0, padding:0, border:'none', background:'none', color:T.accent, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:T.font }}>
              {descExpanded ? '▲ Mostra meno' : '▼ Mostra descrizione completa'}
            </button>
          </div>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:12 }}>
        {[['Proponibili', proponibiliCount], ['Score medio', scoreAvg], ['Score massimo', scoreMax], ['Miglior fitting', topFitting]].map(([lbl, val]) => (
          <div key={lbl} style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:10, padding:'12px 14px' }}>
            <div style={{ fontSize:22, fontWeight:600, lineHeight:1.1 }}>{val}</div>
            <div style={{ fontSize:11, color:T.txt3, marginTop:3 }}>{lbl}</div>
          </div>
        ))}
      </div>
      {/* Richiesto da UC-MF-02: "il conteggio della longlist, con
          distinzione tra numero complessivo di risorse e numero di
          risorse in stato Proponibile" — due numeri distinti, non uno
          solo. longlistLoading copre la finestra in cui l'arricchimento
          non è ancora arrivato, per non mostrare 0 come se fosse un dato vero. */}
      <div style={{ fontSize:12, color:T.txt3, marginBottom:24 }}>
        {longlistLoading
          ? 'Caricamento popolazione valutabile…'
          : `${filtered.length} ${filtered.length === 1 ? 'risorsa complessiva' : 'risorse complessive'} · ${proponibiliCount} in stato Proponibile`}
      </div>
      {longlistErrore && (() => {
        // Il 404 «non più pubblicato su Altamira» è definitivo: riprovare non serve.
        const annuncioChiuso = /non più pubblicato/i.test(longlistErrore);
        return (
          <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:10, background:T.redBg, color:T.redTxt, fontSize:12.5 }}>
            ⚠ {annuncioChiuso ? 'Annuncio non più aperto su Altamira: nessuna longlist disponibile.' : longlistErrore}
            {!annuncioChiuso && (
              <>{' '}<button onClick={() => setLonglistTick(t => t + 1)} style={{ border:'none', background:'none', color:T.redTxt, textDecoration:'underline', cursor:'pointer', fontSize:12.5, padding:0 }}>Riprova</button></>
            )}
          </div>
        );
      })()}

      {/* ── Filtri di visualizzazione ── */}
      <div style={{ marginBottom:20, padding:14, borderRadius:12, background:T.surface, border:`0.5px solid ${T.border}`, display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:4, background:T.surface2, borderRadius:8, padding:3 }}>
          {[['tutte','Tutte'], ['diretta','Solo candidature dirette'], ['pool','Solo pool']].map(([val,lbl]) => (
            <button key={val} onClick={() => { setVistaFiltro(val); setPage(1); }}
              style={{ padding:'6px 12px', borderRadius:6, border:'none', fontSize:12, fontWeight: vistaFiltro===val?600:400, background: vistaFiltro===val?T.surface:'transparent', color: vistaFiltro===val?T.accent:T.txt2, cursor:'pointer', boxShadow: vistaFiltro===val?'0 1px 2px rgba(0,0,0,0.08)':'none' }}>
              {lbl}
            </button>
          ))}
        </div>
        {/* Condizioni dichiarate nel Self-Assessment (06/10): filtrate dal
            server, non in memoria — la longlist si ricarica a ogni scelta. */}
        {FILTRI_CONDIZIONI.map(([param, lbl, campo]) => (
          <label key={param} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.txt2 }}>
            {lbl}
            <select value={filtriCondizioni[param]}
              onChange={e => { const v = e.target.value; setFiltriCondizioni(f => ({ ...f, [param]: v })); setPage(1); }}
              style={{ height:30, padding:'0 6px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:12, fontFamily:T.font, background:T.surface, color:T.txt }}>
              <option value="">Tutte</option>
              {Object.entries(ETICHETTE_CONDIZIONI[campo]).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        ))}
        {filtriCondizioniAttivi && (
          <>
            <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:T.txt2 }}
              title="Tiene in lista anche chi non ha dichiarato il campo filtrato nel Self-Assessment">
              <input type="checkbox" checked={ancheNonDichiarati} onChange={e => { setAncheNonDichiarati(e.target.checked); setPage(1); }} />
              anche non dichiarati
            </label>
            <button onClick={() => { setFiltriCondizioni({ disponibile: '', sede: '', trasferte: '', tipo_rapporto: '' }); setPage(1); }}
              style={{ border:'none', background:'none', color:T.accent, fontSize:12, cursor:'pointer', padding:0, fontFamily:T.font }}>
              Azzera condizioni
            </button>
          </>
        )}
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
          <input type="text" placeholder="Cerca candidato…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:T.txt2 }}>Score min</span>
          <input type="number" min="0" max="100" placeholder="0" value={minScore} onChange={e => { setMinScore(e.target.value); setPage(1); }}
            style={{ height:32, width:64, padding:'0 8px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, outline:'none' }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:12, color:T.txt3 }}>{filtered.length} {filtered.length === 1 ? 'candidato' : 'candidati'}</span>
          <ExportLonglistMenu righe={filtered} annuncio={ann} filtri={{ vista: vistaFiltro, search, minScore, ...filtriCondizioni, anche_non_dichiarati: ancheNonDichiarati }} />
        </div>
      </div>

      {(longlistLoading) ? (
        <div style={{ padding:'2rem', textAlign:'center', color:T.txt3, background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14 }}>Caricamento…</div>
      ) : (
      <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:T.surface2 }}>
              {[['#','',36],['Candidato','candidate_name',null],['Score','hybrid_score',null],['Fitting','fitting',null],['Regione','localita',null],['Condizioni SA','',null],['Origine','origine',null],['Stato risorsa','stato_ciclo_vita',null],['Voto HR','voto_hr',null],['Stato ultimo SA','',null],['','',40],['Invia SA','',110]].map(([h,f,w]) => (
                <th key={h} onClick={() => f && toggleSort(f)}
                  style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:600, color: sortField===f ? T.accent : T.txt2, borderBottom:`0.5px solid ${T.border}`, whiteSpace:'nowrap', cursor:f?'pointer':'default', userSelect:'none', width:w||undefined, letterSpacing:'0.03em' }}>
                  {h}{sortField===f ? (sortDir==='desc' ? ' ↓' : ' ↑') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr><td colSpan={12} style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>
                {criteriAttivi.length > 0 ? `Nessun candidato corrisponde ai criteri applicati: ${criteriAttivi.join(' · ')}` : 'Nessun candidato trovato'}
              </td></tr>
            )}
            {pageRows.map((r, i) => {
              const globalRank = (page-1)*PAGE_SIZE + i + 1;
              return (
                <tr key={r.cvid || i} onClick={() => onOpenPanel(r)}
                  style={{ borderBottom:`0.5px solid ${T.border}`, cursor:'pointer', transition:'background 0.1s', background: r.status === 'ERROR' ? '#fff9f9' : T.surface }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = r.status === 'ERROR' ? '#fff9f9' : T.surface}
                >
                  <td style={{ padding:'11px 12px' }}><Rank n={globalRank} /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar name={r.candidate_name} />
                      <span style={{ fontWeight:500 }}>{r.candidate_name}</span>
                    </div>
                  </td>
                  <td style={{ padding:'11px 12px', minWidth:120 }}>
                    {(r.origine === 'pool' || r.has_score) ? <ScoreFill score={r.hybrid_score} /> : <span style={{ color:T.txt3 }}>—</span>}
                  </td>
                  <td style={{ padding:'11px 12px' }}>
                    {(r.origine === 'pool' || r.has_score) ? <FittingBadge fitting={r.fitting} /> : <span style={{ color:T.txt3 }}>—</span>}
                  </td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:T.txt2 }}>{r.localita || '—'}</td>
                  <td style={{ padding:'11px 12px', fontSize:11.5, color:T.txt2, maxWidth:220 }}>
                    {/* Condizioni dichiarate nel Self-Assessment (06/10):
                        prima riga le scelte chiuse, su cui si filtra; seconda
                        i testi liberi. Il dettaglio intero nel title. */}
                    {CONDIZIONI_SA.some(k => r[k]) ? (() => {
                      const chiuse = [
                        r.disponibile_opportunita && `Disp. ${etichettaCondizione('disponibile_opportunita', r.disponibile_opportunita)}`,
                        r.sede && etichettaCondizione('sede', r.sede),
                        r.trasferte && `Trasferte ${etichettaCondizione('trasferte', r.trasferte).toLowerCase()}`,
                        r.tipo_rapporto && etichettaCondizione('tipo_rapporto', r.tipo_rapporto),
                      ].filter(Boolean);
                      const libere = [
                        r.area_geografica,
                        r.preavviso && `preavviso ${r.preavviso}`,
                        r.aspettativa_economica && `asp. ${r.aspettativa_economica}`,
                      ].filter(Boolean);
                      const dettaglio = [
                        ['Disponibile a nuove opportunità', 'disponibile_opportunita'], ['Sede', 'sede'],
                        ['Area geografica', 'area_geografica'], ['Trasferte', 'trasferte'],
                        ['Rapporto preferito', 'tipo_rapporto'], ['Preavviso', 'preavviso'],
                        ['Aspettativa economica', 'aspettativa_economica'],
                      ].filter(([, k]) => r[k]).map(([l, k]) => `${l}: ${etichettaCondizione(k, r[k])}`);
                      if (r.condizioni_il) dettaglio.push(`Dichiarate il ${formatDateLabel(r.condizioni_il)}`);
                      return (
                        <div title={dettaglio.join('\n')}>
                          {chiuse.length > 0 && <div style={{ color:T.txt }}>{chiuse.join(' · ')}</div>}
                          {libere.length > 0 && (
                            <div style={{ fontSize:11, color:T.txt3, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{libere.join(' · ')}</div>
                          )}
                        </div>
                      );
                    })() : <span style={{ color:T.txt3 }} title="Nessuna condizione dichiarata nel Self-Assessment">—</span>}
                  </td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:T.txt2 }}>
                    {r.origine === 'diretta' ? 'Candidatura diretta' : 'Pool'}
                  </td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:T.txt2 }}>
                    {/* Arricchito da longlistMap (/hr/longlist) — undefined
                        quando la persona non è mai stata trovata lì,
                        genuinamente non collegata a un candidato. Non
                        indovinato, non un trattino generico: distingue
                        davvero "non ancora sappiamo" da "sappiamo, è R2". */}
                    {r.stato_ciclo_vita
                      ? (STATO_RISORSA_LABEL[r.stato_ciclo_vita === 'R3' ? `R3${r.sottostato?.slice(-1) || 'a'}` : r.stato_ciclo_vita] || r.stato_ciclo_vita)
                      : <span title="Nessun candidato TalentGPT collegato a questo cvid">—</span>
                    }
                  </td>
                  {/* 23/10 — SAL 16/10, azione 24: Voto HR accanto allo stato. */}
                  <td style={{ padding:'11px 12px' }}><VotoHrBadge voto={r.voto_hr} /></td>
                  {/* 05/10 — SAL 02/10 (azione 23): tolte le colonne "Stato" (badge
                      Valutato/Scoring non pronto/Nessun match, residuo della vecchia
                      dashboard) e "Profilo SA" (codice SA, ridondante con lo stato
                      risorsa). */}
                  <td style={{ padding:'11px 12px' }}>
                    {(() => {
                      const invito = r.ultimo_invito || null;
                      const STATO_SA_STILE = {
                        'Inviato': { bg: T.accentBg, fg: T.accentTxt },
                        'In compilazione': { bg: T.amberBg, fg: T.amberTxt },
                        'Compilato': { bg: T.greenBg, fg: T.greenTxt },
                        'Scaduto': { bg: T.redBg, fg: T.redTxt },
                      };
                      const stile = STATO_SA_STILE[r.stato_ultimo_sa] || { bg: T.grayBg, fg: T.grayTxt };
                      // Due righe: etichetta dello stato (badge) e, sotto, la data
                      // (invio o inizio compilazione), cosi' la colonna resta stretta.
                      const etichetta = r.stato_ultimo_sa || 'Non inviato';
                      const dettaglio = r.stato_ultimo_sa === 'Inviato' && invito?.created_at
                        ? `il ${formatDataOra(invito.created_at)}`
                        : r.stato_ultimo_sa === 'In compilazione' && invito?.consumed_at
                          ? `dal ${formatDataOra(invito.consumed_at)}`
                          : null;
                      return (
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', gap:2 }}>
                          <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500, background:stile.bg, color:stile.fg, whiteSpace:'nowrap' }}>
                            {etichetta}
                          </span>
                          {dettaglio && (
                            <span style={{ fontSize:10.5, color:T.txt3, whiteSpace:'nowrap', paddingLeft:2 }}>{dettaglio}</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ padding:'11px 12px' }}>
                    <button onClick={e => { e.stopPropagation(); onOpenPanel(r); }}
                      style={{ height:26, padding:'0 8px', fontSize:11, border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor:'pointer', fontFamily:T.font }}>👁</button>
                  </td>
                  <td style={{ padding:'11px 12px' }} onClick={e => e.stopPropagation()}>
                    {/* Invio Self-Assessment unificato (29/09) — checkbox per
                        candidato_id, non cvid (vedi nota su handleInviaSelezionati
                        sopra). V22 (nessun SA già aperto) e V32 (invio recente,
                        regola vincolante AF §4.1) applicati qui. */}
                    {!r.candidato_id ? (
                      <span style={{ fontSize:11, color:T.txt3 }} title="Nessun candidato TalentGPT collegato">—</span>
                    ) : giaInvitato(r) ? (
                      // UAT-26 (UC-SA-01, FA4): l'istanza aperta non si reinvia, si
                      // sollecita. Il clic apre la scheda, dove il sollecito è
                      // proposto con data e stato dell'istanza.
                      <label
                        style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, color:T.txt3, fontStyle:'italic', whiteSpace:'nowrap', cursor:'pointer' }}
                        title={`${r.ultimo_invito?.created_at ? `Inviato il ${formatDataOra(r.ultimo_invito.created_at)}` : 'Self-Assessment già inviato'}${r.ultimo_sollecito ? ` · ultimo sollecito il ${formatDataOra(r.ultimo_sollecito)}` : ''}. Selezionala per inviare un sollecito: nuovo link sulla stessa conversazione, scadenza invariata.`}>
                        <input
                          type="checkbox"
                          checked={selectedSollecito.has(r.candidato_id)}
                          onChange={() => {
                            setSelectedSollecito((prev) => {
                              const next = new Set(prev);
                              if (next.has(r.candidato_id)) next.delete(r.candidato_id);
                              else next.add(r.candidato_id);
                              return next;
                            });
                            setPreviewInvio(null);
                            setPreviewSollecito(null);
                            setSendInvioSuccess(null);
                          }}
                        />
                        Già inviato · sollecito
                      </label>
                    ) : invitatoDiRecente(r) ? (
                      <span style={{ fontSize:11, color:T.amberTxt, fontStyle:'italic' }}
                        title={`${r.ultimo_invito?.created_at ? `Ultimo invio: ${formatDataOra(r.ultimo_invito.created_at)}. ` : ''}Inviato meno di ${SOGLIA_INVIO_RECENTE_GIORNI} giorni fa: procedere per contatto diretto.`}>
                        Recente
                      </span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={selectedInvio.has(r.candidato_id)}
                        disabled={!r.invitabile}
                        title={!r.invitabile ? (r.motivo_non_invitabile || 'non invitabile') : ''}
                        onChange={() => {
                          setSelectedInvio((prev) => {
                            const next = new Set(prev);
                            if (next.has(r.candidato_id)) next.delete(r.candidato_id);
                            else next.add(r.candidato_id);
                            return next;
                          });
                          setPreviewInvio(null);
                          setPreviewSollecito(null);
                          setSendInvioSuccess(null);
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div style={{ padding:'10px 16px', borderTop:`0.5px solid ${T.border}`, display:'flex', alignItems:'center', gap:6, justifyContent:'center' }}>
            <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
              style={{ height:28, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor: page===1?'not-allowed':'pointer', opacity: page===1?0.5:1, fontFamily:T.font }}>‹</button>
            <span style={{ fontSize:12, color:T.txt2 }}>Pag. {page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
              style={{ height:28, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor: page===totalPages?'not-allowed':'pointer', opacity: page===totalPages?0.5:1, fontFamily:T.font }}>›</button>
          </div>
        )}
      </div>
      )}

      {/* ── SEZIONE 3: Storico proponibili (prototipo) ──
          Nascosta temporaneamente il 22/09, su richiesta esplicita — non
          eliminata: evita la domanda "perché ci sono 5000 persone qui?" in
          demo. Il dato resta corretto e recuperabile (basta rimettere il
          blocco sotto), la sezione stessa non ha altri problemi propri —
          il blocco vero (fit_score vuota) è identico ovunque in MultiFitting,
          non specifico di questa sezione.

          ATTENZIONE — non confondere con il badge "Storico" di StatusBadge
          (REUSED) nelle tabelle principali: sono due concetti diversi,
          nonostante il nome uguale. Questa sezione usa un endpoint separato
          (/api/multifitting/historical/{id}), mai riverificato il 24/09
          quando abbiamo corretto l'altro. Il commento sotto (1 giugno
          2026/MAXIFILTER_KEEP_REVIEW) potrebbe essere ancora corretto per
          QUESTA fonte specifica, o potrebbe essere altrettanto superato —
          non dato per scontato, semplicemente non controllato oggi. */}
      {false && (<>
      {/* ── SEZIONE 3 originale ──
          Punteggi dallo screening massivo iniziale (MAXIFILTER_KEEP_REVIEW,
          1 giugno 2026), non presenti nel pool nightly perché la vista
          v_altamira_nightly_job_multifitting filtra solo su
          ALTAMIRA_NIGHTLY_NEW_APPLICATION. Dato già calcolato, non ricalcolato
          al volo — ma potenzialmente non aggiornato (CV o JD cambiati dopo
          il batch): da qui il badge "Storico" su ogni riga.
          Collassata di default: sezione secondaria, non serve al primo
          sguardo su un annuncio. */}
      <div
        onClick={() => setHistoricalCollapsed(v => !v)}
        style={{ display:'flex', alignItems:'center', gap:12, margin:'28px 0 8px', cursor:'pointer', userSelect:'none' }}
      >
        <div style={{ flex:1, height:'0.5px', background:T.borderMd }} />
        <h3 style={{ fontSize:13, fontWeight:700, color:T.txt2, margin:0, whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:11, color:T.txt3, transition:'transform 0.15s', display:'inline-block', transform: historicalCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}>▸</span>
          📚 Storico proponibili
          {historicalCollapsed && historicalRows.length > 0 && (
            <span style={{ fontSize:11, fontWeight:500, color:T.txt3 }}>({historicalRows.length})</span>
          )}
        </h3>
        <div style={{ flex:1, height:'0.5px', background:T.borderMd }} />
      </div>

      {!historicalCollapsed && (
        <>
          <p style={{ fontSize:11, color:T.txt3, textAlign:'center', margin:'0 0 12px' }}>
            Punteggi dello screening iniziale (1 giugno 2026) su tutto il pool proponibili, non ancora incluso nel run nightly — dato storico, non ricalcolato di recente.
          </p>

          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
            <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
              <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
              <input type="text" placeholder="Cerca candidato…" value={historicalSearch} onChange={e => setHistoricalSearch(e.target.value)}
                style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:12, color:T.txt2 }}>Score min</span>
              <input type="number" min="0" max="100" placeholder="0" value={historicalMinScore} onChange={e => setHistoricalMinScore(e.target.value)}
                style={{ height:32, width:64, padding:'0 8px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, outline:'none' }} />
            </div>
            <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:12, color:T.txt3 }}>{historicalFiltered.length} {historicalFiltered.length === 1 ? 'candidato' : 'candidati'}</span>
            </div>
          </div>

          {historicalLoading ? (
            <div style={{ padding:'2rem', textAlign:'center', color:T.txt3, background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14 }}>Caricamento…</div>
          ) : (
            <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:T.surface2 }}>
                <th style={{ padding:'9px 8px', width:28, borderBottom:`0.5px solid ${T.border}` }}>
                  <input
                    type="checkbox"
                    checked={historicalFiltered.length > 0 && historicalFiltered.every(r => selectedSA.has(r.cvid))}
                    onChange={() => {
                      const tutteSelezionate = historicalFiltered.every(r => selectedSA.has(r.cvid));
                      setSelectedSA(prev => {
                        const next = new Set(prev);
                        historicalFiltered.forEach(r => tutteSelezionate ? next.delete(r.cvid) : next.add(r.cvid));
                        return next;
                      });
                    }}
                  />
                </th>
                {[['#',36],['Candidato',null],['Score',null],['Fitting',null],['Regione',null],['Stato',null],['',40]].map(([h,w]) => (
                  <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:600, color:T.txt2, borderBottom:`0.5px solid ${T.border}`, whiteSpace:'nowrap', userSelect:'none', width:w||undefined, letterSpacing:'0.03em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {historicalFiltered.length === 0 && (
                <tr><td colSpan={8} style={{ padding:'2rem', textAlign:'center', color:T.txt3 }}>Nessun punteggio storico disponibile</td></tr>
              )}
              {historicalFiltered.map((r, i) => (
                <tr key={i} onClick={() => onOpenPanel(r)}
                  style={{ borderBottom:`0.5px solid ${T.border}`, cursor:'pointer', transition:'background 0.1s', background: T.surface }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = T.surface}
                >
                  <td style={{ padding:'11px 8px' }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedSA.has(r.cvid)} onChange={() => toggleSelectedSA(r.cvid)} />
                  </td>
                  <td style={{ padding:'11px 12px' }}><Rank n={i + 1} /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar name={r.candidate_name} />
                      <span style={{ fontWeight:500 }}>{r.candidate_name}</span>
                    </div>
                  </td>
                  <td style={{ padding:'11px 12px', minWidth:120 }}><ScoreFill score={r.hybrid_score} /></td>
                  <td style={{ padding:'11px 12px' }}><FittingBadge fitting={r.fitting} /></td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:T.txt2 }}>{r.localita || '—'}</td>
                  <td style={{ padding:'11px 12px' }}><StatusBadge status="REUSED" /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <button onClick={e => { e.stopPropagation(); onOpenPanel(r); }}
                      style={{ height:26, padding:'0 8px', fontSize:11, border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor:'pointer', fontFamily:T.font }}>👁</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}
      </>)}

      {/* ── Barra invio Self-Assessment unificata ── 29/09 (UC-SA-01/WF-MF-02):
          sostituisce sia la vecchia barra qui sotto (disattivata dal 24/09,
          selezionava per cvid — mai riconosciuto da /hr/invite) sia il
          pannello D1/Console HR separato (HrSelfAssessmentConsole.jsx),
          rimosso da questa pagina: stessa popolazione, stessa azione, un
          solo punto d'invio anche visivamente, non solo lato backend.
          selectedInvio è per candidato_id, come richiesto da InvitoIn. */}
      {totaleSelezionati > 0 && (
        <div style={{ position:'sticky', bottom:16, marginTop:16, padding:14, borderRadius:12, background:'#1A1916', color:'#fff', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap', boxShadow:'0 4px 16px rgba(0,0,0,0.25)' }}>
          <span style={{ fontSize:13, fontWeight:600 }}>
            {totaleSelezionati} {totaleSelezionati === 1 ? 'candidato selezionato' : 'candidati selezionati'}
            {selectedSollecito.size > 0 && <span style={{ fontWeight:400 }}> ({selectedSollecito.size} {selectedSollecito.size === 1 ? 'sollecito' : 'solleciti'})</span>}
          </span>
          <button
            onClick={handlePreviewInvio}
            disabled={previewInvioLoading}
            style={{ padding:'7px 14px', borderRadius:8, border:'0.5px solid rgba(255,255,255,0.3)', background:'transparent', color:'#fff', fontSize:12.5, fontWeight:600, cursor: previewInvioLoading ? 'default' : 'pointer' }}
          >
            {previewInvioLoading ? 'Anteprima…' : 'Anteprima selezione'}
          </button>
          {previewInvio && previewInvio.known && (
            <>
              <span style={{ fontSize:12.5 }}>
                <strong style={{ color:'#8FD68A' }}>{previewInvio.inviati ?? '—'}</strong> da invitare
                {previewInvio.destinatari?.length > 0 && <> ({ripartizionePerProfilo(previewInvio.destinatari)})</>}
                {previewSollecito && previewSollecito.ok.length > 0 && <>, <strong style={{ color:'#8FD68A' }}>{previewSollecito.ok.length}</strong> da sollecitare</>}
                {previewSollecito && previewSollecito.esclusi.length > 0 && (
                  <span style={{ color:'#F2A0A0' }}> · non sollecitabili: {previewSollecito.esclusi.map((e) => `${e.nome} (${e.motivo})`).join('; ')}</span>
                )}
              </span>
              <button
                onClick={handleConfirmInvio}
                disabled={confirmInvioLoading || !((previewInvio.inviati || 0) + (previewSollecito?.ok.length || 0))}
                style={{ padding:'7px 16px', borderRadius:8, border:'none', background:T.accent, color:'#fff', fontSize:12.5, fontWeight:600, cursor: confirmInvioLoading ? 'default' : 'pointer' }}
              >
                {confirmInvioLoading ? 'Invio in corso…' : 'Conferma invio'}
              </button>
              {/* 15/10 — FE-05 (SIT-042): annulla la conferma e mantiene la selezione. */}
              <button
                onClick={() => { setPreviewInvio(null); setPreviewSollecito(null); }}
                disabled={confirmInvioLoading}
                style={{ padding:'7px 12px', borderRadius:8, border:'0.5px solid rgba(255,255,255,0.3)', background:'transparent', color:'#fff', fontSize:12.5, cursor:'pointer' }}
              >
                Annulla
              </button>
            </>
          )}
          <button
            onClick={() => { setSelectedInvio(new Set()); setSelectedSollecito(new Set()); setPreviewInvio(null); setPreviewSollecito(null); }}
            style={{ padding:'7px 12px', borderRadius:8, border:'0.5px solid rgba(255,255,255,0.3)', background:'transparent', color:'#fff', fontSize:12.5, cursor:'pointer' }}
          >
            Annulla selezione
          </button>
          {sendInvioError && (
            <span style={{ fontSize:12.5, color:'#F2A0A0' }}>⚠ {sendInvioError}</span>
          )}
        </div>
      )}
      {sendInvioSuccess && (
        <div style={{ marginTop:16, padding:'10px 14px', borderRadius:10, background:T.greenBg, color:T.greenTxt, fontSize:12.5 }}>
          ✓ Invio eseguito.
          {sendInvioSuccess.known && (
            <> {sendInvioSuccess.inviati ?? '—'} invitati, {sendInvioSuccess.saltati ?? '—'} saltati.</>
          )}
          {sendInvioSuccess.solleciti > 0 && <> {sendInvioSuccess.solleciti} {sendInvioSuccess.solleciti === 1 ? 'sollecito inviato' : 'solleciti inviati'}.</>}
          {sendInvioSuccess.sollecitiFalliti?.length > 0 && (
            <span style={{ color:T.redTxt }}> Solleciti non riusciti: {sendInvioSuccess.sollecitiFalliti.map((f) => `${f.nome} (${f.motivo})`).join('; ')}.</span>
          )}
        </div>
      )}

      {/* Ricerca estesa — NASCOSTA il 22/09, stesso giorno in cui è stata
          costruita: ripensandoci, il valore pratico è debole. Trovare
          qualcuno nel bacino Altamira grezzo non porta a nessuna azione
          possibile — il 97% di questa popolazione non è nemmeno un
          candidato tracciato in TalentGPT, e l'invio fallirebbe comunque
          (fit_score vuota, nessun candidato su cui appoggiarsi). Più un
          cruscotto informativo che uno strumento operativo. Codice
          intatto, spento con {false &&}, facile da riattivare se emerge
          un uso concreto che oggi non vediamo. */}
      {false && <RicercaEstesaAnnuncio jdId={ann.id} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPALE
═══════════════════════════════════════════════════════════ */
// 15/10 — FE-04: nome leggibile del questionario deciso dal sistema (mai il
// codice SA-xx a schermo), per la ripartizione nel riepilogo di invio.
const NOME_PROFILO_SA = {
  SA1: 'Primo contatto (In qualifica)',
  SA2A: 'Proponibile mai staffato',
  SA2B: 'Collaboratore staffato',
  SA2C: 'Collaboratore ex-staffato',
  SA4: 'Chiusura esperienza',
};
const nomeProfiloSa = (p) => NOME_PROFILO_SA[String(p || '').toUpperCase().replace(/[^A-Z0-9]/g, '')] || 'Profilo non indicato';
function ripartizionePerProfilo(lista) {
  const conti = {};
  for (const x of lista || []) {
    const n = nomeProfiloSa(x?.profilo_sa ?? x?.profilo);
    conti[n] = (conti[n] || 0) + 1;
  }
  return Object.entries(conti).map(([n, c]) => `${c} ${n}`).join(' · ');
}

export default function MultifittingDashboard() {
  const navigate = useNavigate();

  const [rows, setRows]                   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [view, setView]                   = useState('annunci'); // default: Annunci
  const [selectedAnn, setSelectedAnn]     = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [selectedRow, setSelectedRow]     = useState(null);
  // Aggiunto il 26/09 — forza il refetch di longlistMap dentro
  // ScreenCandidatiAnn quando lo stato di una risorsa cambia dal
  // pannello di dettaglio. I due componenti vivono a livelli separati
  // dell'albero (SidePanel qui, longlistMap dentro ScreenCandidatiAnn) —
  // incrementare questo numero è il modo più semplice per farli
  // comunicare senza un refactor più ampio dello stato.
  const [refreshKey, setRefreshKey]       = useState(0);
  const [startDate, setStartDate]         = useState('');   // '' = nessun filtro
  const [endDate, setEndDate]             = useState('');   // '' = nessun filtro
  const [dateBounds, setDateBounds]       = useState({ min: null, max: null });
  const [consipProfiles, setConsipProfiles] = useState([]);

  // Carica i profili Consip una volta sola (tabella statica, non legata al periodo)
  useEffect(() => {
    axios.get('/api/multifitting/consip-profiles')
      .then(r => setConsipProfiles(r.data || []))
      .catch(() => setConsipProfiles([]));
  }, []);

  // Carica i confini temporali dei dati disponibili (solo per i limiti min/max
  // dei date picker, non più per un selettore a tendina)
  useEffect(() => {
    axios.get('/api/multifitting/runs')
      .then(r => {
        const dates = r.data || []; // ordinate DESC dal backend
        if (dates.length > 0) setDateBounds({ min: dates[dates.length - 1], max: dates[0] });
      })
      .catch(() => {});
  }, []);

  // Fetch dati: default nessun filtro (tutto lo storico). Se è impostata solo
  // la data di inizio, la fine è implicitamente oggi (stessa regola applicata
  // anche lato backend, qui solo per coerenza visiva dei filtri mostrati).
  const effectiveEndDate = (startDate && !endDate) ? new Date().toISOString().slice(0, 10) : endDate;

  const fetchData = useCallback(() => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (effectiveEndDate) params.end_date = effectiveEndDate;
    setLoading(true);
    setError(null);
    axios.get('/api/multifitting/nightly', { params })
      .then(r => setRows(r.data || []))
      .catch(e => {
        if (e.response?.status === 403) navigate('/');
        else setError('Errore nel caricamento dei dati. Riprova.');
      })
      .finally(() => setLoading(false));
  }, [startDate, effectiveEndDate, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleStartDateChange(newDate) {
    setStartDate(newDate);
    setSelectedAnn(null);
    setSelectedCluster(null);
    setView('annunci');
  }

  function handleEndDateChange(newDate) {
    setEndDate(newDate);
    setSelectedAnn(null);
    setSelectedCluster(null);
    setView('annunci');
  }

  function handleResetDates() {
    setStartDate('');
    setEndDate('');
    setSelectedAnn(null);
    setSelectedCluster(null);
    setView('annunci');
  }

  function handleSelectAnn(ann) {
    setSelectedAnn(ann);
    setView('ann-cand');
    window.scrollTo(0, 0);
  }

  function handleBack() {
    setSelectedAnn(null);
    setView('annunci');
    setSelectedRow(null);
    window.scrollTo(0, 0);
  }

  function handleSelectCluster(cluster) {
    setSelectedCluster(cluster);
    setView('cluster-cand');
    window.scrollTo(0, 0);
  }

  function handleBackCluster() {
    setSelectedCluster(null);
    setView('cluster');
    setSelectedRow(null);
    window.scrollTo(0, 0);
  }

  function switchTopView(target) {
    setSelectedAnn(null);
    setSelectedCluster(null);
    setSelectedRow(null);
    setView(target);
    window.scrollTo(0, 0);
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, fontSize:14, color:T.txt }}>
      {/* Navbar */}
      <nav style={{ background:T.surface, borderBottom:`0.5px solid ${T.border}`, padding:'0 28px', height:52, display:'flex', alignItems:'center', gap:14, position:'sticky', top:0, zIndex:50 }}>
        <span style={{ fontSize:13, fontWeight:600, letterSpacing:'0.05em', color:T.accent }}>AISA TalentGPT</span>
        <span style={{ color:T.borderMd }}>›</span>
        <span style={{ fontSize:13, color:T.txt2 }}>
          {view === 'cluster'      ? 'MultiFitting · Cluster' :
           view === 'cluster-cand' ? `MultiFitting · ${selectedCluster?.title || ''}` :
           view === 'annunci'      ? 'MultiFitting · Annunci' :
                                      `MultiFitting · ${selectedAnn?.title || ''}`}
        </span>

        {/* Toggle vista Cluster / Annunci */}
        <div style={{ display:'flex', alignItems:'center', gap:2, background:T.surface2, border:`0.5px solid ${T.border}`, borderRadius:6, padding:2 }}>
          <button onClick={() => switchTopView('cluster')}
            style={{
              height:26, padding:'0 12px', borderRadius:4, border:'none', fontSize:12, fontFamily:T.font, cursor:'pointer',
              background: (view==='cluster'||view==='cluster-cand') ? T.surface : 'transparent',
              color: (view==='cluster'||view==='cluster-cand') ? T.txt : T.txt2,
              fontWeight: (view==='cluster'||view==='cluster-cand') ? 600 : 400,
              boxShadow: (view==='cluster'||view==='cluster-cand') ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}>
            🗂️ Cluster
          </button>
          <button onClick={() => switchTopView('annunci')}
            style={{
              height:26, padding:'0 12px', borderRadius:4, border:'none', fontSize:12, fontFamily:T.font, cursor:'pointer',
              background: (view==='annunci'||view==='ann-cand') ? T.surface : 'transparent',
              color: (view==='annunci'||view==='ann-cand') ? T.txt : T.txt2,
              fontWeight: (view==='annunci'||view==='ann-cand') ? 600 : 400,
              boxShadow: (view==='annunci'||view==='ann-cand') ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}>
            📋 Annunci
          </button>
        </div>

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>

          {/* Selettore periodo */}
          <div style={{ display:'flex', alignItems:'center', gap:6, background:T.surface2, border:`0.5px solid ${T.border}`, borderRadius:6, padding:'0 10px', height:32 }}>
            <span style={{ fontSize:11, color:T.txt3 }}>Dal</span>
            <input type="date" value={startDate} min={dateBounds.min || undefined} max={dateBounds.max || undefined}
              onChange={e => handleStartDateChange(e.target.value)}
              style={{ height:28, border:'none', background:'transparent', fontSize:12, fontFamily:T.font, color:T.txt, cursor:'pointer', outline:'none' }} />
            <span style={{ fontSize:11, color:T.txt3 }}>Al</span>
            <input type="date" value={endDate} min={startDate || dateBounds.min || undefined} max={dateBounds.max || undefined}
              onChange={e => handleEndDateChange(e.target.value)}
              style={{ height:28, border:'none', background:'transparent', fontSize:12, fontFamily:T.font, color:T.txt, cursor:'pointer', outline:'none' }} />
            {(startDate || endDate) && (
              <button onClick={handleResetDates} title="Rimuovi filtro periodo"
                style={{ height:20, width:20, border:'none', background:'none', color:T.txt3, cursor:'pointer', fontSize:14, lineHeight:1, padding:0 }}>✕</button>
            )}
          </div>

          <button onClick={fetchData}
            style={{ height:32, padding:'0 13px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, fontSize:13, cursor:'pointer', fontFamily:T.font }}>
            ↻
          </button>
          <button onClick={() => navigate('/')}
            style={{ height:32, padding:'0 13px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, fontSize:13, cursor:'pointer', fontFamily:T.font }}>
            ← Hub
          </button>
        </div>
      </nav>

      {/* Main */}
      {/* 29/09: la vista 'ann-cand' (tabella candidati/annuncio, ora con le
          colonne Profilo SA / Stato ultimo SA / Invia SA) è più larga delle
          altre — maxWidth allargato solo qui invece di forzare uno scroll
          orizzontale scomodo sulla tabella. */}
      <div style={{ padding:'24px 28px 48px', maxWidth: view === 'ann-cand' ? 1360 : 1100, margin:'0 auto', width:'100%' }}>
        {loading && <div style={{ padding:'4rem', textAlign:'center', color:T.txt3 }}>Caricamento…</div>}
        {!loading && error && (
          <div style={{ padding:'1.5rem', borderRadius:8, background:T.redBg, color:T.redTxt }}>{error}</div>
        )}
        {!loading && !error && view === 'cluster' && (
          <ScreenCluster rows={rows} consipProfiles={consipProfiles} onSelectCluster={handleSelectCluster} />
        )}
        {!loading && !error && view === 'cluster-cand' && selectedCluster && (
          <ScreenCandidatiCluster rows={rows} cluster={selectedCluster} startDate={startDate} endDate={endDate} consipProfiles={consipProfiles} onBack={handleBackCluster} onOpenPanel={setSelectedRow} />
        )}
        {!loading && !error && view === 'annunci' && (
          <ScreenAnnunci rows={rows} onSelectAnn={handleSelectAnn} />
        )}
        {!loading && !error && view === 'ann-cand' && selectedAnn && (
          <ScreenCandidatiAnn rows={rows} ann={selectedAnn} startDate={startDate} endDate={endDate} onBack={handleBack} onOpenPanel={setSelectedRow} refreshKey={refreshKey} />
        )}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>
            Nessun dato disponibile per il periodo selezionato.
          </div>
        )}
      </div>

      {selectedRow && (
        <SidePanel candidate={selectedRow} allRows={rows} onClose={() => setSelectedRow(null)} onResourceUpdated={() => setRefreshKey(k => k + 1)} />
      )}
    </div>
  );
}